import { AnimatePresence, motion } from 'framer-motion';
import {
  Activity,
  Award,
  Bell,
  Bookmark,
  Briefcase,
  CalendarDays,
  Camera,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Clock,
  Copy,
  FileText,
  Gift,
  Home,
  LifeBuoy,
  ListTodo,
  Lock,
  MapPin,
  MessageCircle,
  Mic,
  Play,
  Plus,
  Search,
  Send,
  Settings,
  Shield,
  Sparkles,
  StickyNote,
  Tag,
  User,
  Video,
  Volume2,
  X,
} from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { GGJLogoMark } from '../../Brand/GGJLogo';

import { HeroPreviewCopyProvider, useHeroPreviewCopy } from './useHeroPreviewCopy';
import { useHeroMockJobs } from './usePreviewJobFixtures';

const TYPING_MS = 28;
const PAUSE_AFTER_TYPE_MS = 700;
const PHASE_SEARCH_MS = 2400;
const PHASE_MATCHES_MS = 4200;
const PAUSE_LOOP_MS = 2200;
const COMPANION_TYPING_MS = 32;
const COMPANION_THINK_MS = 1400;
const COMPANION_TURN_PAUSE_MS = 1800;
const COMPANION_LOOP_PAUSE_MS = 3200;

type Phase = 'typing' | 'searching' | 'matches';

type PreviewViewId =
  | 'home'
  | 'jobs'
  | 'agent'
  | 'calendar'
  | 'companion'
  | 'prepare'
  | 'activity'
  | 'referrals'
  | 'support'
  | 'settings';

type NavItem = {
  id: PreviewViewId;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
};

type MockJob = {
  title: string;
  company: string;
  location: string;
  score: number;
  skills: string[];
  saved?: boolean;
  remote?: boolean;
  jobType?: string;
  experienceLevel?: string;
  postedLabel?: string;
  source?: 'linkedin' | 'naukri' | 'peerlist';
  description?: string;
};

function jobKey(job: MockJob) {
  return `${job.title}-${job.company}`;
}

function PreviewMatchBadge({ score }: { score: number }) {
  const p = useHeroPreviewCopy();
  const style =
    score >= 75
      ? { background: '#dcfce3', color: '#166534' }
      : score >= 55
        ? { background: '#eef2ff', color: '#4338ca' }
        : { background: '#f1f5f9', color: '#475569' };

  return (
    <span
      className="inline-flex shrink-0 items-center gap-0.5 rounded-full px-1.5 py-0.5 leading-none"
      style={style}
    >
      <span className="text-[8px] font-bold uppercase tracking-wide opacity-80">{p.matchLabel}</span>
      <span className="text-[11px] font-black leading-none">{score}%</span>
    </span>
  );
}

function PreviewMatchRing({ score, ringId }: { score: number; ringId: string }) {
  const p = useHeroPreviewCopy();
  const size = 36;
  const radius = 14;
  const center = size / 2;
  const circumference = 2 * Math.PI * radius;
  const dash = (Math.max(0, Math.min(100, score)) / 100) * circumference;
  const gradientId = `hero-preview-match-${ringId.replace(/[^a-zA-Z0-9_-]/g, '')}`;

  return (
    <div className="home-hero-preview-match-ring relative h-9 w-9 shrink-0" aria-label={`${score} percent match`}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="block" aria-hidden>
        <defs>
          <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#22d3ee" />
            <stop offset="50%" stopColor="#6C47FF" />
            <stop offset="100%" stopColor="#7c3aed" />
          </linearGradient>
        </defs>
        <circle cx={center} cy={center} r={radius} fill="none" stroke="rgba(148, 163, 184, 0.22)" strokeWidth="2.5" />
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke={`url(#${gradientId})`}
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeDasharray={`${dash} ${circumference}`}
          transform={`rotate(-90 ${center} ${center})`}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center leading-none">
        <span className="text-[4px] font-bold uppercase tracking-wide text-slate-400">{p.matchLabel}</span>
        <span className="text-[8px] font-black text-gray-950 dark:text-white">{score}%</span>
      </div>
    </div>
  );
}

function PreviewJobGridCard({
  job,
  selected = false,
  onSelect,
  fillHeight = false,
  matchStyle = 'badge',
  previewTarget,
}: {
  job: MockJob;
  selected?: boolean;
  onSelect?: () => void;
  fillHeight?: boolean;
  matchStyle?: 'badge' | 'ring';
  previewTarget?: string;
}) {
  const p = useHeroPreviewCopy();
  const initial = job.company.charAt(0).toUpperCase();
  const visibleSkills = job.skills.slice(0, 3);
  const extraSkills = Math.max(0, job.skills.length - 3);
  const missingSkills = job.skills.slice(0, 2);
  const sourceLabel = job.source === 'naukri' ? 'Naukri' : job.source === 'peerlist' ? 'Peerlist' : 'LinkedIn';
  const id = jobKey(job);
  const showRing = matchStyle === 'ring' && job.score >= 25;

  return (
    <article
      role="button"
      tabIndex={0}
      data-preview-target={previewTarget}
      onClick={onSelect}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onSelect?.();
        }
      }}
      className={`home-hero-preview-job-card home-hero-preview-job-card--portrait ${selected ? 'home-hero-preview-job-card--selected' : ''} ${
        showRing ? 'home-hero-preview-job-card--with-ring' : ''
      } ${fillHeight ? 'flex h-full min-h-0 flex-col' : ''}`}
    >
      {showRing ? (
        <div className="absolute right-1 top-1 z-[1]">
          <PreviewMatchRing score={job.score} ringId={id} />
        </div>
      ) : null}

      <div className="relative shrink-0">
        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-50 text-[11px] font-bold text-indigo-600 dark:bg-indigo-950/40 dark:text-indigo-300">
          {initial}
        </div>
        {!showRing && job.score >= 25 ? (
          <div className="absolute -right-1 -top-1">
            <PreviewMatchBadge score={job.score} />
          </div>
        ) : null}
      </div>

      <div className={`mt-1.5 min-w-0 shrink-0 ${showRing ? 'pr-9' : ''}`}>
        <p className="line-clamp-2 text-[11px] font-bold leading-snug text-gray-950 dark:text-white">{job.title}</p>
        <p className="mt-0.5 line-clamp-1 text-[10px] font-medium text-slate-600 dark:text-gray-400">{job.company}</p>
        <p className="mt-0.5 text-[9px] leading-snug text-slate-400">
          {sourceLabel}
          {job.postedLabel ? ` · ${job.postedLabel}` : ''}
        </p>
        {(job.experienceLevel || job.jobType) && (
          <p className="mt-0.5 text-[9px] font-medium text-slate-500">
            {[job.experienceLevel, job.jobType].filter(Boolean).join(' · ')}
          </p>
        )}
      </div>

      <div className="home-hero-preview-job-card__tags mt-1.5 flex min-w-0 flex-col gap-0.5">
        {visibleSkills.map((skill) => (
          <span key={skill} className="home-hero-preview-job-card__tag w-full truncate">
            {skill}
          </span>
        ))}
        {extraSkills > 0 ? (
          <span className="home-hero-preview-job-card__tag home-hero-preview-job-card__tag--more w-fit">
            {p.jobsExtraSkills.replace('{{count}}', String(extraSkills))}
          </span>
        ) : null}
      </div>

      {job.description ? (
        <p
          className={`home-hero-preview-job-card__body mt-1.5 min-w-0 text-[10px] leading-relaxed text-slate-600 dark:text-gray-400 ${
            fillHeight ? 'line-clamp-3 min-h-0 flex-1' : 'line-clamp-2'
          }`}
        >
          {job.description}
        </p>
      ) : fillHeight ? (
        <div className="min-h-0 flex-1" />
      ) : null}

      {missingSkills.length > 0 ? (
        <p className="mt-1.5 shrink-0 text-[9px] leading-snug text-slate-500">
          <span className="font-semibold text-slate-600">{p.jobsMissingLabel}</span> {missingSkills.join(', ')}
        </p>
      ) : null}

      <p className="mt-1 shrink-0 text-[9px] leading-snug text-slate-400">
        {[job.location, job.remote ? p.jobsRemoteFriendly : null].filter(Boolean).join(' · ')}
      </p>

      <div className={`home-hero-preview-job-card__footer mt-1.5 flex items-center justify-between gap-1 ${fillHeight ? 'mt-auto shrink-0' : ''}`}>
        <p className="truncate text-[9px] text-slate-400">{job.postedLabel ?? p.jobsRecentlyPosted}</p>
        <div className="flex shrink-0 items-center gap-0.5">
          {job.saved ? <Bookmark className="h-3.5 w-3.5 fill-slate-400 text-slate-400" /> : null}
          <span className="home-hero-preview-job-card__arrow" aria-hidden>
            <ChevronRight className="h-3.5 w-3.5" strokeWidth={2.25} />
          </span>
        </div>
      </div>
    </article>
  );
}

function PreviewJobsMoreCard() {
  const p = useHeroPreviewCopy();
  return (
    <div className="home-hero-preview-jobs-more flex shrink-0 items-center justify-between gap-2 px-2 py-1.5">
      <div className="min-w-0 text-left">
        <p className="text-[10px] font-bold text-indigo-700 dark:text-indigo-300">{p.jobsMoreMatches}</p>
        <p className="text-[9px] text-slate-500 dark:text-gray-400">{p.jobsMoreMatchesSub}</p>
      </div>
      <span className="inline-flex shrink-0 items-center gap-0.5 text-[10px] font-semibold text-indigo-600 dark:text-indigo-400">
        {p.jobsBrowseAll}
        <ChevronRight className="h-3.5 w-3.5" strokeWidth={2.25} />
      </span>
    </div>
  );
}

function NavButton({
  item,
  active,
  onClick,
}: {
  item: NavItem;
  active: boolean;
  onClick: () => void;
}) {
  const Icon = item.icon;
  return (
    <button
      type="button"
      data-preview-nav={item.id}
      onClick={onClick}
      className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-[11px] transition-colors ${
        active
          ? 'border border-gray-200/80 bg-white font-semibold text-gray-950 shadow-sm dark:border-gray-700/60 dark:bg-white/10 dark:text-white'
          : 'font-medium text-gray-500 hover:bg-white/60 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-white/5 dark:hover:text-gray-200'
      }`}
    >
      <Icon className={`h-3.5 w-3.5 shrink-0 ${active ? 'text-indigo-600 dark:text-indigo-400' : ''}`} />
      <span className="truncate text-left">{item.label}</span>
    </button>
  );
}

function PreviewHeader({
  icon: Icon,
  title,
  live,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  live?: boolean;
}) {
  return (
    <div className="flex shrink-0 items-center gap-2 border-b border-gray-50 px-3 py-2 dark:border-gray-800">
      <Icon className="h-3.5 w-3.5 shrink-0 text-indigo-600 dark:text-indigo-400" />
      <span className="text-[11px] font-semibold text-gray-900 dark:text-white">{title}</span>
      {live ? <span className="ml-auto h-1.5 w-1.5 shrink-0 animate-pulse rounded-full bg-emerald-500" /> : null}
    </div>
  );
}

function MiniCard({
  children,
  className = '',
  onClick,
  previewTarget,
}: {
  children: React.ReactNode;
  className?: string;
  onClick?: () => void;
  previewTarget?: string;
}) {
  const Tag = onClick ? 'button' : 'div';
  return (
    <Tag
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      data-preview-target={previewTarget}
      className={`rounded-lg border border-gray-100 bg-white dark:border-gray-800 dark:bg-gray-900/50 ${
        onClick ? 'w-full text-left transition-colors' : ''
      } ${className}`}
    >
      {children}
    </Tag>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="mb-1 px-0.5 text-[10px] font-bold uppercase tracking-wider text-gray-400 lg:text-[8px]">{children}</p>
  );
}

function PreviewSettingsSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="home-hero-preview-settings-section mb-2 min-w-0">
      <h3 className="text-[10px] font-semibold text-gray-950 dark:text-white">{title}</h3>
      <div className="mb-1.5 mt-0.5 border-b-2 border-gray-200 dark:border-gray-700" />
      {children}
    </section>
  );
}

function PreviewLabelRow({
  label,
  readOnly = false,
  children,
}: {
  label: string;
  readOnly?: boolean;
  children: React.ReactNode;
}) {
  const p = useHeroPreviewCopy();
  return (
    <div className="grid grid-cols-[74px_minmax(0,1fr)] items-center gap-1.5 py-0.5">
      <dt className="text-[9px] font-medium leading-snug text-gray-500 dark:text-gray-400">
        {label}
        {readOnly ? <span className="block text-[8px] text-gray-400">{p.settingsReadOnly}</span> : null}
      </dt>
      <dd className="min-w-0">{children}</dd>
    </div>
  );
}

function PreviewSettingsField({
  value,
  readOnly = false,
  masked = false,
}: {
  value: string;
  readOnly?: boolean;
  masked?: boolean;
}) {
  return (
    <div
      className={`home-hero-preview-settings-field truncate text-[9px] ${
        readOnly ? 'home-hero-preview-settings-field--readonly' : ''
      } ${masked ? 'tracking-widest text-gray-400' : ''}`}
    >
      {value}
    </div>
  );
}

function PreviewSettingsButton({
  label,
  primary = true,
  icon: Icon,
  previewTarget,
}: {
  label: string;
  primary?: boolean;
  icon?: React.ComponentType<{ className?: string }>;
  previewTarget?: string;
}) {
  return (
    <button
      type="button"
      data-preview-target={previewTarget}
      className={`inline-flex items-center gap-1 rounded-md px-2 py-1 text-[9px] font-semibold ${
        primary
          ? 'bg-indigo-600 text-white dark:bg-indigo-500'
          : 'border border-gray-200 bg-gray-100 text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200'
      }`}
    >
      {Icon ? <Icon className="h-3 w-3" /> : null}
      {label}
    </button>
  );
}

function PreviewJobCard({
  job,
  compact = false,
  applyLabel,
}: {
  job: MockJob;
  compact?: boolean;
  applyLabel: string;
}) {
  const p = useHeroPreviewCopy();
  const initial = job.company.charAt(0).toUpperCase();
  return (
    <div className={`rounded-lg border border-gray-100 bg-white dark:border-gray-800 dark:bg-gray-900/60 ${compact ? 'p-2' : 'p-2.5'}`}>
      <div className="flex gap-2">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-indigo-50 text-[10px] font-bold text-indigo-600 dark:bg-indigo-950/40 dark:text-indigo-300">
          {initial}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-1.5">
            <p className="truncate text-[10px] font-bold text-gray-950 dark:text-white">{job.title}</p>
            <span className="shrink-0 rounded-md bg-emerald-50 px-1.5 py-0.5 text-[8px] font-black text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300">
              {job.score}%
            </span>
          </div>
          <p className="truncate text-[8px] text-gray-500 dark:text-gray-400">{job.company}</p>
          <p className="mt-0.5 flex items-center gap-0.5 text-[8px] text-gray-400">
            <MapPin className="h-2.5 w-2.5 shrink-0" />
            <span className="truncate">{job.location}{job.remote ? ` · ${p.jobsFilterRemote}` : ''}</span>
          </p>
          {!compact && (
            <div className="mt-1.5 flex flex-wrap gap-0.5">
              {job.skills.slice(0, 3).map((skill) => (
                <span key={skill} className="rounded bg-gray-100 px-1 py-0.5 text-[7px] font-medium text-gray-600 dark:bg-gray-800 dark:text-gray-300">
                  {skill}
                </span>
              ))}
            </div>
          )}
          <div className="mt-2 flex items-center gap-1">
            <button type="button" className="rounded-md bg-indigo-600 px-2 py-0.5 text-[8px] font-semibold text-white">
              {applyLabel}
            </button>
            <button type="button" className="rounded-md border border-gray-200 px-2 py-0.5 text-[8px] font-medium text-gray-600 dark:border-gray-700 dark:text-gray-300">
              {p.jobCardSave}
            </button>
            {job.saved ? <Bookmark className="ml-auto h-3 w-3 fill-amber-400 text-amber-400" /> : null}
          </div>
        </div>
      </div>
    </div>
  );
}

function useMockJobs(): MockJob[] {
  return useHeroMockJobs();
}

const ATS_TREND_VALUES = [39, 55, 60, 68, 72, 72] as const;

function AtsComplianceTrendChart({ values }: { values: readonly number[] }) {
  const p = useHeroPreviewCopy();
  const width = 120;
  const height = 42;
  const padX = 4;
  const padY = 4;
  const minVal = Math.min(...values) - 8;
  const maxVal = Math.max(...values) + 4;
  const range = Math.max(1, maxVal - minVal);
  const delta = values[values.length - 1] - values[0];

  const points = values.map((value, index) => {
    const x = padX + (index / Math.max(1, values.length - 1)) * (width - padX * 2);
    const y = padY + (1 - (value - minVal) / range) * (height - padY * 2);
    return { x, y, value };
  });

  const linePath = points.map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`).join(' ');
  const areaPath = `${linePath} L ${points[points.length - 1].x} ${height - 1} L ${points[0].x} ${height - 1} Z`;
  const last = points[points.length - 1];

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex items-center justify-between gap-1">
        <p className="text-[7px] font-bold uppercase tracking-wider text-gray-400">{p.atsComplianceTrend}</p>
        <span className="rounded-full bg-emerald-50 px-1 py-px text-[5px] font-bold text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300">
          +{delta} {p.pointsAbbrev}
        </span>
      </div>
      <div className="relative mt-0.5 min-h-0 flex-1">
        <svg viewBox={`0 0 ${width} ${height}`} className="h-full w-full overflow-visible" preserveAspectRatio="none" aria-hidden>
          <defs>
            <linearGradient id="heroAtsTrendFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#6366f1" stopOpacity="0.28" />
              <stop offset="100%" stopColor="#6366f1" stopOpacity="0.03" />
            </linearGradient>
          </defs>
          {[0.33, 0.66].map((pct) => (
            <line
              key={pct}
              x1={0}
              y1={height * pct}
              x2={width}
              y2={height * pct}
              stroke="currentColor"
              strokeWidth="0.35"
              className="text-gray-100 dark:text-gray-800"
            />
          ))}
          <path d={areaPath} fill="url(#heroAtsTrendFill)" />
          <path
            d={linePath}
            fill="none"
            stroke="#6366f1"
            strokeWidth="1.4"
            strokeLinecap="round"
            strokeLinejoin="round"
            vectorEffect="non-scaling-stroke"
          />
          {points.slice(0, -1).map((point, index) => (
            <circle key={index} cx={point.x} cy={point.y} r="1.1" fill="#fff" stroke="#6366f1" strokeWidth="0.7" />
          ))}
          <circle cx={last.x} cy={last.y} r="2" fill="#6366f1" stroke="#fff" strokeWidth="0.8" />
        </svg>
        <span
          className="absolute rounded bg-indigo-600 px-0.5 py-px text-[5px] font-bold leading-none text-white"
          style={{ left: `${((last.x / width) * 100).toFixed(1)}%`, top: `${Math.max(0, ((last.y / height) * 100) - 18)}%`, transform: 'translate(-50%, -100%)' }}
        >
          {last.value}%
        </span>
      </div>
      <div className="mt-0.5 flex justify-between gap-0.5">
        {(['J', 'F', 'M', 'A', 'Y', 'J'] as const).map((month, index) => (
          <span
            key={`${month}-${index}`}
            className={`flex-1 text-center text-[5px] ${index === 5 ? 'font-bold text-indigo-600 dark:text-indigo-400' : 'text-gray-400'}`}
          >
            {month}
          </span>
        ))}
      </div>
    </div>
  );
}

function MetricBarRow({
  label,
  value,
  barClass,
}: {
  label: string;
  value: number;
  barClass: string;
}) {
  return (
    <div className="w-full">
      <div className="flex items-center justify-between gap-1 text-[5px]">
        <span className="truncate text-gray-600 dark:text-gray-400">{label}</span>
        <span className="shrink-0 font-bold text-gray-900 dark:text-white">{value}%</span>
      </div>
      <div className="mt-0.5 h-1.5 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-gray-800">
        <div className={`h-full rounded-full ${barClass}`} style={{ width: `${value}%` }} />
      </div>
    </div>
  );
}

function JobActivitySparkline({ values }: { values: readonly number[] }) {
  const p = useHeroPreviewCopy();
  const width = 120;
  const height = 28;
  const pad = 3;
  const max = Math.max(...values, 1);
  const points = values.map((value, index) => {
    const x = pad + (index / Math.max(1, values.length - 1)) * (width - pad * 2);
    const y = height - pad - (value / max) * (height - pad * 2);
    return `${index === 0 ? 'M' : 'L'} ${x} ${y}`;
  }).join(' ');

  return (
    <div>
      <div className="mb-0.5 flex items-center justify-between">
        <p className="text-[6px] font-semibold text-gray-600 dark:text-gray-300">{p.applicationsTrend}</p>
        <span className="text-[5px] font-bold text-emerald-600">↑ 24%</span>
      </div>
      <svg viewBox={`0 0 ${width} ${height}`} className="h-7 w-full" preserveAspectRatio="none" aria-hidden>
        <path d={`${points} L ${width - pad} ${height - pad} L ${pad} ${height - pad} Z`} fill="url(#heroAppsSparkFill)" opacity="0.9" />
        <path d={points} fill="none" stroke="#0ea5e9" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
        <defs>
          <linearGradient id="heroAppsSparkFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#0ea5e9" stopOpacity="0.22" />
            <stop offset="100%" stopColor="#0ea5e9" stopOpacity="0.02" />
          </linearGradient>
        </defs>
      </svg>
    </div>
  );
}

function PipelineAnalyticsPanel({ stats }: { stats: { apps: string; interviews: string; offers: string } }) {
  const p = useHeroPreviewCopy();
  const stages = [
    { label: p.pipelineStageApplied, count: 12, tone: 'bg-sky-500/85' },
    { label: p.pipelineStageScreen, count: 5, tone: 'bg-indigo-500/85' },
    { label: p.pipelineStageInterview, count: 2, tone: 'bg-amber-500/85' },
    { label: p.pipelineStageOffer, count: 1, tone: 'bg-emerald-500/85' },
  ];
  const maxCount = Math.max(...stages.map((stage) => stage.count));

  return (
    <div className="flex h-full min-h-0 flex-col gap-1">
      <div className="flex items-start justify-between gap-1">
        <div>
          <p className="text-[7px] font-semibold text-gray-700 dark:text-gray-300">{p.pipelineFunnelTitle}</p>
          <p className="text-[6px] text-gray-400">{p.pipelineLast6Months}</p>
        </div>
        <div className="flex gap-0.5">
          {[
            { value: stats.apps, label: p.pipelineChipApps },
            { value: stats.interviews, label: p.pipelineChipInt },
            { value: stats.offers, label: p.pipelineChipOff },
          ].map((chip) => (
            <span key={chip.label} className="rounded bg-gray-50 px-1 py-0.5 text-center dark:bg-gray-900/40">
              <span className="block text-[7px] font-bold leading-none text-gray-900 dark:text-white">{chip.value}</span>
              <span className="text-[4px] uppercase text-gray-400">{chip.label}</span>
            </span>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-4 gap-0.5">
        {stages.map((stage) => (
          <div key={stage.label} className="flex flex-col items-center rounded-md border border-gray-50 bg-gray-50/70 px-0.5 py-1 dark:border-gray-800 dark:bg-gray-900/40">
            <div className="flex h-8 w-full items-end justify-center">
              <div
                className={`w-3/4 rounded-t-sm ${stage.tone}`}
                style={{ height: `${Math.max(20, (stage.count / maxCount) * 100)}%` }}
              />
            </div>
            <p className="mt-0.5 text-[8px] font-bold leading-none text-gray-900 dark:text-white">{stage.count}</p>
            <p className="text-[5px] text-gray-400">{stage.label}</p>
          </div>
        ))}
      </div>

      <JobActivitySparkline values={[5, 6, 4, 8, 7, 12]} />

      <div className="grid grid-cols-2 gap-0.5">
        <div className="rounded-md border border-gray-50 px-1 py-0.5 dark:border-gray-800">
          <p className="text-[5px] text-gray-400">{p.pipelineResponseRate}</p>
          <p className="text-[7px] font-bold text-gray-900 dark:text-white">17%</p>
        </div>
        <div className="rounded-md border border-gray-50 px-1 py-0.5 dark:border-gray-800">
          <p className="text-[5px] text-gray-400">{p.pipelineAvgReplyTime}</p>
          <p className="text-[7px] font-bold text-gray-900 dark:text-white">{p.pipelineAvgReplySample}</p>
        </div>
      </div>

      <div className="mt-auto space-y-0.5 border-t border-gray-100 pt-1 dark:border-gray-800">
        <p className="text-[5px] font-bold uppercase tracking-wide text-gray-400">{p.pipelineThisWeek}</p>
        <div className="flex flex-wrap gap-0.5">
          {[p.pipelineWeekMetaFollowUp, p.pipelineWeekStripeScreen, p.pipelineWeekAmazonPrep].map((item) => (
            <span key={item} className="rounded-full bg-indigo-50 px-1 py-px text-[5px] font-medium text-indigo-700 dark:bg-indigo-950/40 dark:text-indigo-300">
              {item}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

function MarketCompetitivenessPanel() {
  const p = useHeroPreviewCopy();
  const score = 69;

  return (
    <div className="flex h-full w-full flex-col gap-1">
      <div className="flex w-full items-center justify-between gap-1 rounded-md bg-gradient-to-r from-indigo-50 to-violet-50 px-1.5 py-1 dark:from-indigo-950/40 dark:to-violet-950/30">
        <div>
          <p className="text-[13px] font-black leading-none text-gray-950 dark:text-white">{score}%</p>
          <p className="mt-0.5 text-[5px] text-gray-500">{p.marketOverallScore}</p>
        </div>
        <span className="rounded-full bg-white/90 px-1.5 py-0.5 text-[6px] font-bold text-emerald-700 shadow-sm dark:bg-emerald-950/50 dark:text-emerald-300">
          {p.marketStrong}
        </span>
      </div>

      <div className="space-y-1">
        <MetricBarRow label={p.marketAtsScore} value={80} barClass="bg-indigo-500" />
        <MetricBarRow label={p.marketKeywords} value={66} barClass="bg-violet-500" />
        <MetricBarRow label={p.marketFormat} value={88} barClass="bg-teal-500" />
      </div>

      <div className="mt-auto space-y-0.5 border-t border-gray-100 pt-1 dark:border-gray-800">
        <p className="text-[5px] font-bold uppercase tracking-wide text-gray-400">{p.marketVsMarket}</p>
        {[
          { label: p.marketYou, value: score, tone: 'bg-indigo-500' },
          { label: p.marketAvg, value: 54, tone: 'bg-gray-300 dark:bg-gray-600' },
          { label: p.marketTop10, value: 85, tone: 'bg-emerald-500' },
        ].map((row) => (
          <div key={row.label} className="flex w-full items-center gap-1">
            <span className="w-7 shrink-0 text-[5px] text-gray-500">{row.label}</span>
            <div className="h-1 min-w-0 flex-1 overflow-hidden rounded-full bg-gray-100 dark:bg-gray-800">
              <div className={`h-full rounded-full ${row.tone}`} style={{ width: `${row.value}%` }} />
            </div>
            <span className="w-4 shrink-0 text-right text-[5px] font-bold text-gray-700 dark:text-gray-300">{row.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ResumeHealthPanel() {
  const p = useHeroPreviewCopy();
  const rows = [
    { label: p.resumeAtsPassRate, pct: 80, barClass: 'bg-emerald-500', status: p.resumeStatusGood, statusClass: 'text-emerald-600' },
    { label: p.resumeKeywordCoverage, pct: 66, barClass: 'bg-amber-500', status: p.resumeStatusFix, statusClass: 'text-amber-600' },
    { label: p.resumeFormatQuality, pct: 88, barClass: 'bg-emerald-500', status: p.resumeStatusGood, statusClass: 'text-emerald-600' },
  ];

  return (
    <div className="flex h-full w-full min-h-0 flex-col gap-1">
      <div className="space-y-1">
        {rows.map((row) => (
          <div key={row.label} className="w-full">
            <div className="flex items-center justify-between gap-1 text-[5px]">
              <span className="truncate text-gray-600 dark:text-gray-400">{row.label}</span>
              <span className={`shrink-0 font-bold ${row.statusClass}`}>{row.status}</span>
            </div>
            <div className="mt-0.5 flex items-center gap-1">
              <div className="h-1.5 min-w-0 flex-1 overflow-hidden rounded-full bg-gray-100 dark:bg-gray-800">
                <div className={`h-full rounded-full ${row.barClass}`} style={{ width: `${row.pct}%` }} />
              </div>
              <span className="w-5 shrink-0 text-right text-[5px] font-bold text-gray-900 dark:text-white">{row.pct}%</span>
            </div>
          </div>
        ))}
      </div>

      <div className="rounded-md border border-amber-100 bg-amber-50/60 px-1 py-0.5 dark:border-amber-900/30 dark:bg-amber-950/20">
        <p className="text-[5px] font-bold uppercase text-amber-700 dark:text-amber-300">{p.resumeImproveNext}</p>
        <p className="text-[5px] leading-snug text-gray-700 dark:text-gray-300">{p.resumeImproveHint}</p>
      </div>

      <div className="mt-auto space-y-0.5">
        <p className="text-[5px] font-bold uppercase tracking-wide text-gray-400">{p.resumeUpcoming}</p>
        {[
          { line: p.resumeUpcomingInterview, tone: 'bg-amber-500' },
          { line: p.resumeUpcomingFollowUp, tone: 'bg-sky-500' },
        ].map((item) => (
          <div key={item.line} className="flex w-full items-center gap-1 rounded-md border border-gray-50 px-1 py-0.5 dark:border-gray-800">
            <span className={`h-1 w-1 shrink-0 rounded-full ${item.tone}`} />
            <p className="truncate text-[5px] text-gray-700 dark:text-gray-300">{item.line}</p>
          </div>
        ))}
      </div>

      <div className="rounded-md bg-emerald-50 px-1 py-0.5 dark:bg-emerald-950/30">
        <p className="text-[5px] font-semibold text-emerald-700 dark:text-emerald-300">{p.resumeInterviewOffers}</p>
      </div>
    </div>
  );
}

function DashboardPreview({ jobs }: { jobs: MockJob[] }) {
  const HERO_PREVIEW = useHeroPreviewCopy();
  const p = HERO_PREVIEW;
  const readiness = 72;
  const actions = [
    { icon: FileText, label: p.dashboardActionOptimize, sub: p.dashboardActionOptimizeSub, color: '#10b981' },
    { icon: Search, label: p.dashboardActionFindJobs, sub: p.dashboardActionFindJobsSub, color: '#0ea5e9' },
    { icon: FileText, label: p.dashboardActionCoverLetter, sub: p.dashboardActionCoverLetterSub, color: '#f59e0b' },
    { icon: Mic, label: p.dashboardActionInterview, sub: p.dashboardActionInterviewSub, color: '#a855f7' },
    { icon: Tag, label: p.dashboardActionTrackPipeline, sub: p.dashboardActionTrackPipelineSub, color: '#14b8a6' },
    { icon: Sparkles, label: p.dashboardActionApply, sub: p.dashboardActionApplySub, color: '#6C47FF' },
  ];
  const activityItems = [
    { line: p.activityApplied, meta: p.time7hAgo, tone: 'bg-emerald-500' },
    { line: p.activitySampleCompany, meta: p.activityAppliedMeta, tone: 'bg-emerald-500' },
    { line: p.activityOptimized, meta: p.timeYesterday, tone: 'bg-indigo-500' },
    { line: p.activityInterview, meta: p.timeMon3pm, tone: 'bg-amber-500' },
    { line: p.activityCoverLetterDrafted, meta: p.time3dAgo, tone: 'bg-purple-500' },
  ];

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden">
      <PreviewHeader icon={Home} title={p.breadcrumbHome} />
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden p-2">
        {/* Mobile — stacked cards, no dense 4-column grid */}
        <div className="home-hero-preview-dashboard-mobile flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto lg:hidden">
          <MiniCard className="overflow-hidden p-0">
            <div className="flex items-center justify-between gap-2 p-2.5">
              <div>
                <p className="text-[9px] font-bold uppercase tracking-wider text-gray-400">{p.dashboardAtsLabel}</p>
                <p className="text-lg font-extrabold leading-none text-gray-950 dark:text-white">{readiness}%</p>
                <span className="mt-1 inline-block rounded-full bg-amber-50 px-2 py-0.5 text-[9px] font-semibold text-amber-700 dark:bg-amber-950/40 dark:text-amber-300">
                  {p.dashboardAtsBadge}
                </span>
              </div>
              <div className="relative h-11 w-11 shrink-0">
                <svg viewBox="0 0 100 100" className="h-full w-full -rotate-90">
                  <circle cx="50" cy="50" r="40" fill="none" stroke="currentColor" strokeWidth="10" className="text-gray-100 dark:text-gray-800" />
                  <circle
                    cx="50"
                    cy="50"
                    r="40"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="10"
                    strokeLinecap="round"
                    className="text-indigo-600 dark:text-indigo-400"
                    strokeDasharray={`${(readiness / 100) * 2 * Math.PI * 40} ${2 * Math.PI * 40}`}
                  />
                </svg>
                <span className="absolute inset-0 flex items-center justify-center text-[9px] font-bold">{readiness}%</span>
              </div>
            </div>
            <div className="border-t border-amber-100/80 bg-amber-50/40 px-2.5 py-1.5 dark:border-amber-900/30 dark:bg-amber-950/20">
              <p className="text-[9px] font-bold uppercase text-amber-700 dark:text-amber-300">{p.dashboardActionSuggested}</p>
              <p className="text-[10px] leading-snug text-gray-700 dark:text-gray-300">{p.dashboardActionBody}</p>
            </div>
          </MiniCard>

          <MiniCard className="flex min-h-[88px] flex-col p-2">
            <AtsComplianceTrendChart values={ATS_TREND_VALUES} />
          </MiniCard>

          <div>
            <SectionLabel>{p.dashboardYourActions}</SectionLabel>
            <MiniCard className="grid grid-cols-2 gap-1 p-1.5">
              {actions.slice(0, 4).map(({ icon: Icon, label, sub, color }, index) => (
                <div
                  key={label}
                  data-preview-target={index === 0 ? 'dashboard-action' : undefined}
                  className="flex flex-col justify-center rounded-md border border-gray-50 bg-gray-50/80 px-1.5 py-1 dark:border-gray-800 dark:bg-gray-900/40"
                >
                  <Icon className="mb-0.5 h-3 w-3 shrink-0" style={{ color }} />
                  <p className="truncate text-[9px] font-bold uppercase leading-tight text-gray-800 dark:text-gray-200">{label}</p>
                  <p className="truncate text-[8px] leading-tight text-gray-400">{sub}</p>
                </div>
              ))}
            </MiniCard>
          </div>

          <div>
            <SectionLabel>{p.activityTitle}</SectionLabel>
            <MiniCard className="flex flex-col gap-1 p-2">
              {activityItems.slice(0, 3).map((item) => (
                <div key={item.line} className="flex items-start gap-2 rounded-md border border-gray-50 bg-gray-50/80 px-2 py-1 dark:border-gray-800 dark:bg-gray-900/40">
                  <span className={`mt-1 h-2 w-2 shrink-0 rounded-full ${item.tone}`} />
                  <div className="min-w-0 flex-1">
                    <p className="text-[10px] font-semibold leading-snug text-gray-800 dark:text-gray-200">{item.line}</p>
                    <p className="text-[9px] text-gray-400">{item.meta}</p>
                  </div>
                </div>
              ))}
            </MiniCard>
          </div>
        </div>

        {/* Desktop — full dashboard grid */}
        <div className="hidden h-full min-h-0 flex-1 flex-col overflow-hidden lg:flex">
        <div className="grid h-full min-h-0 flex-1 grid-rows-[auto_minmax(0,1fr)] gap-1.5">
          {/* Row 1 — compact KPI strip */}
          <div className="grid shrink-0 grid-cols-5 gap-1.5">
            <MiniCard className="col-span-3 overflow-hidden p-0">
              <div className="flex items-center justify-between gap-2 p-2">
                <div>
                  <p className="text-[7px] font-bold uppercase tracking-wider text-gray-400">{p.dashboardAtsLabel}</p>
                  <p className="text-base font-extrabold leading-none text-gray-950 dark:text-white">{readiness}%</p>
                  <span className="mt-0.5 inline-block rounded-full bg-amber-50 px-1.5 py-0.5 text-[6px] font-semibold text-amber-700 dark:bg-amber-950/40 dark:text-amber-300">
                    {p.dashboardAtsBadge}
                  </span>
                </div>
                <div className="relative h-10 w-10 shrink-0">
                  <svg viewBox="0 0 100 100" className="h-full w-full -rotate-90">
                    <circle cx="50" cy="50" r="40" fill="none" stroke="currentColor" strokeWidth="10" className="text-gray-100 dark:text-gray-800" />
                    <circle
                      cx="50"
                      cy="50"
                      r="40"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="10"
                      strokeLinecap="round"
                      className="text-indigo-600 dark:text-indigo-400"
                      strokeDasharray={`${(readiness / 100) * 2 * Math.PI * 40} ${2 * Math.PI * 40}`}
                    />
                  </svg>
                  <span className="absolute inset-0 flex items-center justify-center text-[8px] font-bold">{readiness}%</span>
                </div>
              </div>
              <div className="border-t border-amber-100/80 bg-amber-50/40 px-2 py-1 dark:border-amber-900/30 dark:bg-amber-950/20">
                <p className="text-[6px] font-bold uppercase text-amber-700 dark:text-amber-300">{p.dashboardActionSuggested}</p>
                <p className="text-[8px] leading-snug text-gray-700 dark:text-gray-300">{p.dashboardActionBody}</p>
              </div>
            </MiniCard>
            <MiniCard className="col-span-2 flex min-h-[72px] flex-col p-1.5">
              <AtsComplianceTrendChart values={ATS_TREND_VALUES} />
            </MiniCard>
          </div>

          {/* Row 2 — dense 4-column body */}
          <div className="grid min-h-0 grid-cols-4 gap-1.5">
            {/* Actions + activity timeline */}
            <div className="flex min-h-0 flex-col gap-1.5">
              <div className="flex min-h-0 flex-[1.05] flex-col">
                <SectionLabel>{p.dashboardYourActions}</SectionLabel>
                <MiniCard className="grid h-full min-h-0 grid-cols-2 grid-rows-3 gap-0.5 p-1">
                  {actions.map(({ icon: Icon, label, sub, color }, index) => (
                    <div
                      key={label}
                      data-preview-target={index === 0 ? 'dashboard-action' : undefined}
                      className="flex flex-col justify-center rounded-md border border-gray-50 bg-gray-50/80 px-1 py-0.5 dark:border-gray-800 dark:bg-gray-900/40"
                    >
                      <Icon className="mb-0.5 h-2.5 w-2.5 shrink-0" style={{ color }} />
                      <p className="truncate text-[6px] font-bold uppercase leading-tight text-gray-800 dark:text-gray-200">{label}</p>
                      <p className="truncate text-[5px] leading-tight text-gray-400">{sub}</p>
                    </div>
                  ))}
                </MiniCard>
              </div>
              <div className="flex min-h-0 flex-1 flex-col">
                <SectionLabel>{p.activityTitle}</SectionLabel>
                <MiniCard className="flex h-full min-h-0 flex-col p-1.5">
                  <div className="relative min-h-0 flex-1 space-y-1 pl-2">
                    <div className="absolute bottom-1 left-[3px] top-1 w-px bg-gray-200 dark:bg-gray-700" />
                    {activityItems.map((item) => (
                      <div key={item.line} className="relative flex gap-1.5">
                        <span className={`relative z-10 mt-1 h-1.5 w-1.5 shrink-0 rounded-full ${item.tone}`} />
                        <div className="min-w-0 flex-1 rounded-md border border-gray-50 bg-gray-50/80 px-1 py-0.5 dark:border-gray-800 dark:bg-gray-900/40">
                          <p className="truncate text-[6px] font-semibold leading-tight text-gray-800 dark:text-gray-200">{item.line}</p>
                          <p className="text-[5px] text-gray-400">{item.meta}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </MiniCard>
              </div>
            </div>

            {/* Targeted jobs — compact list fills column */}
            <div className="flex min-h-0 flex-col">
              <SectionLabel>{p.dashboardTargetedJobs}</SectionLabel>
              <MiniCard className="flex h-full min-h-0 flex-col overflow-hidden p-1">
                <div className="min-h-0 flex-1 space-y-0.5 overflow-hidden">
                  {jobs.slice(0, 6).map((job) => (
                    <div
                      key={`${job.title}-${job.company}`}
                      className="shrink-0 rounded-md border border-gray-50 px-1 py-0.5 dark:border-gray-800"
                    >
                      <div className="flex items-start gap-1">
                        <div className="flex h-4 w-4 shrink-0 items-center justify-center rounded bg-indigo-50 text-[6px] font-bold text-indigo-600 dark:bg-indigo-950/40">
                          {job.company.charAt(0)}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-start justify-between gap-0.5">
                            <p className="truncate text-[7px] font-semibold leading-tight text-gray-900 dark:text-white">{job.title}</p>
                            <span className="shrink-0 text-[6px] font-bold text-emerald-600">{job.score}%</span>
                          </div>
                          <p className="truncate text-[6px] leading-tight text-gray-400">{job.company}</p>
                          <p className="truncate text-[5px] leading-tight text-gray-400">{p.jobsMissingLabel} {job.skills.slice(0, 2).join(', ')}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <p className="mt-auto shrink-0 border-t border-gray-100 pt-1 text-center text-[5px] font-semibold text-indigo-600 dark:border-gray-800 dark:text-indigo-400">
                  {p.jobsMoreMatches}
                </p>
              </MiniCard>
            </div>

            {/* Pipeline + analytics */}
            <div className="flex min-h-0 flex-col">
              <SectionLabel>{p.dashboardPipeline}</SectionLabel>
              <MiniCard className="h-full min-h-0 p-1.5">
                <PipelineAnalyticsPanel
                  stats={{ apps: '12', interviews: '2', offers: '1' }}
                />
              </MiniCard>
            </div>

            {/* Market + resume health — side by side, full width */}
            <div className="flex min-h-0 flex-col">
              <SectionLabel>{p.dashboardInsights}</SectionLabel>
              <MiniCard className="h-full min-h-0 p-1.5">
                <div className="grid h-full min-h-0 grid-cols-2 gap-1.5">
                  <div className="flex min-h-0 flex-col border-r border-gray-100 pr-1.5 dark:border-gray-800">
                    <p className="mb-0.5 text-[5px] font-bold uppercase tracking-wide text-gray-400">{p.dashboardMarketLabel}</p>
                    <MarketCompetitivenessPanel />
                  </div>
                  <div className="flex min-h-0 flex-col pl-0.5">
                    <p className="mb-0.5 text-[5px] font-bold uppercase tracking-wide text-gray-400">{p.dashboardResumeLabel}</p>
                    <ResumeHealthPanel />
                  </div>
                </div>
              </MiniCard>
            </div>
          </div>
        </div>
        </div>
      </div>
    </div>
  );
}

function JobsPreview({ jobs }: { jobs: MockJob[] }) {
  const p = useHeroPreviewCopy();
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);
  const [activeFilter, setActiveFilter] = useState(p.jobsFilterAllMatches);
  const [activeTab, setActiveTab] = useState<'all' | 'saved'>('all');
  const gridJobs = jobs.slice(0, 6);
  const filterChips = [p.jobsFilterAllMatches, p.jobsFilterStrongFit, p.jobsFilterRemote, p.jobsFilterFullTime];
  const selectedJob = gridJobs.find((job) => jobKey(job) === selectedJobId);

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden">
      <PreviewHeader icon={Briefcase} title={p.jobsTitle} />
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden px-1.5 pb-1 pt-1">
        <div className="mb-0.5 flex shrink-0 items-center gap-1">
          <button
            type="button"
            onClick={() => setActiveTab('all')}
            className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${
              activeTab === 'all' ? 'bg-gray-900 text-white dark:bg-white dark:text-gray-900' : 'text-gray-500'
            }`}
          >
            {p.jobsTabAll}
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('saved')}
            className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${
              activeTab === 'saved' ? 'bg-gray-900 text-white dark:bg-white dark:text-gray-900' : 'text-gray-500'
            }`}
          >
            {p.jobsTabSaved}
          </button>
        </div>

        <div className="mb-0.5 flex shrink-0 items-center gap-1">
          <div className="flex min-w-0 flex-1 items-center gap-1 rounded-md border border-gray-200 bg-gray-50 px-1 py-0.5 dark:border-gray-700 dark:bg-gray-900/40">
            <Search className="h-3.5 w-3.5 shrink-0 text-gray-400" />
            <span className="truncate text-[10px] text-gray-400">{p.jobsSearchPlaceholder}</span>
          </div>
        </div>

        <div className="mb-0.5 flex shrink-0 flex-wrap items-center gap-0.5">
          {filterChips.map((chip) => (
            <button
              key={chip}
              type="button"
              onClick={() => setActiveFilter(chip)}
              className={`rounded-full border px-2 py-0.5 text-[9px] font-medium transition-colors ${
                activeFilter === chip
                  ? 'border-indigo-200 bg-indigo-50 text-indigo-700 dark:border-indigo-800 dark:bg-indigo-950/40 dark:text-indigo-300'
                  : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300'
              }`}
            >
              {chip}
            </button>
          ))}
          <span className="ml-auto text-[9px] text-gray-400">{p.jobsRecommendedMeta}</span>
        </div>

        <div className="home-hero-preview-jobs-grid flex min-h-0 flex-1 flex-col gap-1.5">
          <div className="grid min-h-0 flex-1 grid-cols-3 grid-rows-2 gap-1.5">
            {gridJobs.map((job, index) => {
              const id = jobKey(job);
              return (
                <PreviewJobGridCard
                  key={id}
                  job={job}
                  fillHeight
                  matchStyle="ring"
                  previewTarget={index === 0 ? 'job-card' : undefined}
                  selected={selectedJobId === id}
                  onSelect={() => setSelectedJobId((current) => (current === id ? null : id))}
                />
              );
            })}
          </div>
          <PreviewJobsMoreCard />
        </div>

        <div className="mt-1.5 shrink-0 rounded-md border border-gray-100 bg-gray-50/80 px-2 py-1.5 dark:border-gray-800 dark:bg-gray-900/40">
          {selectedJob ? (
            <div className="flex items-center justify-between gap-1">
              <div className="min-w-0">
                <p className="truncate text-[10px] font-bold text-gray-900 dark:text-white">
                  {selectedJob.title} · {selectedJob.company}
                </p>
                <p className="truncate text-[9px] text-gray-500">{selectedJob.description}</p>
              </div>
              <span className="shrink-0 rounded-md border border-indigo-200 bg-indigo-50 px-2 py-0.5 text-[9px] font-semibold text-indigo-700 dark:border-indigo-800 dark:bg-indigo-950/40 dark:text-indigo-300">
                {p.jobsOptimizeApply}
              </span>
            </div>
          ) : (
            <div className="flex items-center justify-between gap-1 text-[9px] text-gray-500">
              <span>{p.jobsShowingMatches}</span>
              <span className="font-medium text-gray-700 dark:text-gray-300">{p.jobsSortBestMatch}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function AgentPreview({
  phase,
  displayQuery,
  showCursor,
  searchProgress,
  jobs,
}: {
  phase: Phase;
  displayQuery: string;
  showCursor: boolean;
  searchProgress: number;
  jobs: MockJob[];
}) {
  const { t } = useTranslation();
  const HERO_PREVIEW = useHeroPreviewCopy();
  const cardsVisible = phase === 'matches';
  const feedJobs = jobs.slice(0, 4);

  return (
    <>
      {/* Left — agent chat + typing */}
      <div className="col-span-12 flex h-full min-h-0 flex-col overflow-hidden rounded-none border-r border-gray-200 bg-white dark:border-gray-800 dark:bg-[#0f0f11] md:col-span-5">
        <PreviewHeader icon={Sparkles} title={HERO_PREVIEW.breadcrumbAgent} live />

        <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-hidden p-3">
          <div className="shrink-0 space-y-1">
            <span className="text-[9px] font-bold uppercase tracking-wider text-gray-400">{HERO_PREVIEW.you}</span>
            <div className="min-h-[48px] rounded-xl border border-gray-100 bg-gray-50 px-3 py-2.5 text-[11px] font-medium leading-relaxed text-gray-900 shadow-inner dark:border-gray-800 dark:bg-gray-900/60 dark:text-gray-100">
              {displayQuery}
              {showCursor ? (
                <span className="ml-0.5 inline-block h-3.5 w-[2px] animate-pulse bg-gray-900 align-middle dark:bg-white" />
              ) : null}
            </div>
          </div>

          <AnimatePresence mode="wait">
            {phase === 'searching' && (
              <motion.div key="search" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="space-y-2">
                <span className="text-[9px] font-bold uppercase tracking-wider text-indigo-600 dark:text-indigo-400">
                  {HERO_PREVIEW.agentProcessingLabel}
                </span>
                <div className="rounded-xl border border-indigo-100/50 bg-gradient-to-br from-indigo-50/50 to-sky-50/20 p-3 dark:border-indigo-900/40 dark:from-indigo-950/30 dark:to-sky-950/10">
                  <p className="flex items-center gap-1.5 text-[11px] font-bold text-indigo-950 dark:text-indigo-100">
                    <Search className="h-3.5 w-3.5 shrink-0" />
                    {HERO_PREVIEW.stepSearch}
                  </p>
                  <p className="mt-1 text-[10px] text-gray-600 dark:text-gray-400">{HERO_PREVIEW.agentAnalyzingBody}</p>
                  <div className="mt-2 h-1 overflow-hidden rounded-full bg-indigo-100/70 dark:bg-indigo-900/40">
                    <motion.div className="h-full rounded-full bg-indigo-600 dark:bg-indigo-400" animate={{ width: `${searchProgress}%` }} />
                  </div>
                </div>
              </motion.div>
            )}

            {phase === 'matches' && (
              <motion.div key="agent-reply" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="space-y-2">
                <span className="text-[9px] font-bold uppercase tracking-wider text-indigo-600 dark:text-indigo-400">
                  {HERO_PREVIEW.agentProcessingLabel}
                </span>
                <div className="rounded-xl border border-indigo-100/50 bg-gradient-to-br from-indigo-50/40 to-emerald-50/20 p-3 dark:border-indigo-900/40 dark:from-indigo-950/25 dark:to-emerald-950/15">
                  <p className="text-[11px] font-bold text-gray-950 dark:text-white">{HERO_PREVIEW.agentReplyTitle}</p>
                  <p className="mt-1 text-[10px] text-gray-600 dark:text-gray-400">{HERO_PREVIEW.agentReplyBody}</p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <div className="mt-auto shrink-0 rounded-xl border border-dashed border-gray-200 px-3 py-2 text-[9px] text-gray-400 dark:border-gray-700">
            {HERO_PREVIEW.promptHint}
          </div>
        </div>
      </div>

      {/* Right — match feed grid */}
      <aside className="col-span-12 hidden h-full min-h-0 flex-col overflow-hidden rounded-none border-l border-gray-200 bg-[#fafafa] dark:border-gray-800 dark:bg-[#111113] md:col-span-7 md:flex">
        <div className="flex shrink-0 items-center justify-between border-b border-gray-100 px-3 py-2 dark:border-gray-800">
          <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">
            {HERO_PREVIEW.matchFeed}
          </span>
          <span className="rounded-full bg-emerald-100 px-1.5 py-0.5 text-[9px] font-medium text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300">
            {HERO_PREVIEW.liveUpdates}
          </span>
        </div>
        <div className="flex min-h-0 flex-1 flex-col gap-1.5 overflow-hidden p-2">
          <p className="shrink-0 text-[9px] font-medium text-slate-500 dark:text-gray-400">
            {cardsVisible
              ? t('home.heroPreview.strongMatchesFound', {
                  count: feedJobs.length,
                  defaultValue: HERO_PREVIEW.strongMatchesFound,
                })
              : HERO_PREVIEW.scanningForMatches}
          </p>
          <div className="grid min-h-0 flex-1 grid-cols-2 grid-rows-2 gap-1.5">
            {feedJobs.map((job, index) => {
              const id = jobKey(job);
              return (
                <motion.div
                  key={id}
                  className="min-h-0"
                  initial={{ opacity: 0.15, y: 6 }}
                  animate={{ opacity: cardsVisible ? 1 : index === 0 ? 0.45 : 0.2, y: 0 }}
                  transition={{ delay: cardsVisible ? index * 0.1 : 0 }}
                >
                  <PreviewJobGridCard job={job} fillHeight matchStyle="ring" previewTarget={index === 0 ? 'agent-feed' : undefined} />
                </motion.div>
              );
            })}
          </div>
        </div>
      </aside>
    </>
  );
}

function ActivityPreview({ initialFilter }: { initialFilter?: string }) {
  const HERO_PREVIEW = useHeroPreviewCopy();
  const [activeFilter, setActiveFilter] = useState(initialFilter ?? HERO_PREVIEW.activityFilterAll);
  const filterChips = [
    HERO_PREVIEW.activityFilterAll,
    HERO_PREVIEW.activityFilterApplied,
    HERO_PREVIEW.activityFilterOptimized,
    HERO_PREVIEW.activityFilterInterview,
  ];

  const rows = useMemo(
    () => [
      {
        kind: 'applied' as const,
        job: HERO_PREVIEW.activityRowMarketingJob,
        company: 'Ahsan Solutions',
        action: HERO_PREVIEW.activityActionApplied,
        outcome: HERO_PREVIEW.activityOutcomeApplied,
        score: '—',
        date: HERO_PREVIEW.activityDate2hAgo,
        tone: 'bg-emerald-500',
      },
      {
        kind: 'optimized' as const,
        job: HERO_PREVIEW.activityRowLeadPm,
        company: 'Microsoft',
        action: HERO_PREVIEW.activityActionOptimized,
        outcome: HERO_PREVIEW.activityOutcomeInterview,
        score: '89%',
        date: HERO_PREVIEW.timeYesterday,
        tone: 'bg-indigo-500',
      },
      {
        kind: 'optimized' as const,
        job: HERO_PREVIEW.activityRowSeniorPm,
        company: 'Stripe',
        action: HERO_PREVIEW.activityActionOptimized,
        outcome: HERO_PREVIEW.activityOutcomeUnknown,
        score: '94%',
        date: HERO_PREVIEW.activityDateMon,
        tone: 'bg-indigo-500',
      },
      {
        kind: 'interview' as const,
        job: HERO_PREVIEW.activityRowInterviewPrep,
        company: 'Acme Corp',
        action: HERO_PREVIEW.activityActionInterview,
        outcome: HERO_PREVIEW.activityOutcomeInterview,
        score: '—',
        date: HERO_PREVIEW.activityDateMon3pm,
        tone: 'bg-amber-500',
      },
      {
        kind: 'optimized' as const,
        job: HERO_PREVIEW.activityRowCoverLetterDraft,
        company: 'Meta',
        action: HERO_PREVIEW.activityActionOptimized,
        outcome: HERO_PREVIEW.activityOutcomeApplied,
        score: '86%',
        date: HERO_PREVIEW.time3dAgo,
        tone: 'bg-purple-500',
      },
      {
        kind: 'saved' as const,
        job: HERO_PREVIEW.activityRowSavedMatches,
        company: 'GoGrabJob',
        action: HERO_PREVIEW.activityActionSaved,
        outcome: HERO_PREVIEW.activityOutcomeUnknown,
        score: '—',
        date: HERO_PREVIEW.activityDate4dAgo,
        tone: 'bg-sky-500',
      },
    ],
    [HERO_PREVIEW],
  );

  const filteredRows = rows.filter((row) => {
    if (activeFilter === HERO_PREVIEW.activityFilterAll) return true;
    if (activeFilter === HERO_PREVIEW.activityFilterApplied) return row.kind === 'applied';
    if (activeFilter === HERO_PREVIEW.activityFilterOptimized) return row.kind === 'optimized';
    if (activeFilter === HERO_PREVIEW.activityFilterInterview) return row.kind === 'interview';
    return true;
  });

  const outcomeClass = (outcome: string) => {
    if (outcome === HERO_PREVIEW.activityOutcomeInterview) return 'bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300';
    if (outcome === HERO_PREVIEW.activityOutcomeApplied) return 'bg-indigo-50 text-indigo-700 dark:bg-indigo-950/40 dark:text-indigo-300';
    if (outcome === 'Offer') return 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300';
    return 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300';
  };

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden">
      <PreviewHeader icon={Activity} title={HERO_PREVIEW.activityTitle} />
      <p className="shrink-0 border-b border-gray-50 px-3 pb-2 text-[10px] leading-snug text-gray-500 dark:border-gray-800 dark:text-gray-400">
        {HERO_PREVIEW.activitySubtitle}
      </p>

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden p-2">
        <div className="mb-1 flex shrink-0 flex-wrap items-center gap-0.5">
          {filterChips.map((chip) => (
            <button
              key={chip}
              type="button"
              data-preview-target={chip === HERO_PREVIEW.activityFilterApplied ? 'activity-filter' : undefined}
              onClick={() => setActiveFilter(chip)}
              className={`rounded-full border px-2 py-0.5 text-[9px] font-medium transition-colors ${
                activeFilter === chip
                  ? 'border-indigo-200 bg-indigo-50 text-indigo-700 dark:border-indigo-800 dark:bg-indigo-950/40 dark:text-indigo-300'
                  : 'border-gray-200 bg-white text-gray-600 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300'
              }`}
            >
              {chip}
            </button>
          ))}
          <span className="ml-auto text-[9px] text-gray-400">{HERO_PREVIEW.activityShowing}</span>
        </div>

        <MiniCard className="home-hero-preview-activity-table flex min-h-0 flex-1 flex-col overflow-hidden">
          <div className="grid shrink-0 grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)_0.7fr_0.75fr_0.45fr] gap-1 border-b border-gray-100 bg-gray-50/80 px-2 py-1 text-[8px] font-bold uppercase tracking-wide text-gray-400 dark:border-gray-800 dark:bg-gray-900/40">
            <span>{HERO_PREVIEW.activityColJob}</span>
            <span>{HERO_PREVIEW.activityColCompany}</span>
            <span>{HERO_PREVIEW.activityColAction}</span>
            <span>{HERO_PREVIEW.activityColOutcome}</span>
            <span className="text-right">{HERO_PREVIEW.activityColScore}</span>
          </div>
          <div className="min-h-0 flex-1 overflow-hidden">
            {filteredRows.map((row) => (
              <div
                key={`${row.job}-${row.date}`}
                className="grid grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)_0.7fr_0.75fr_0.45fr] gap-1 border-b border-gray-50 px-2 py-1.5 last:border-0 dark:border-gray-800/80"
              >
                <div className="flex min-w-0 items-start gap-1">
                  <span className={`mt-1 h-1.5 w-1.5 shrink-0 rounded-full ${row.tone}`} />
                  <div className="min-w-0">
                    <p className="truncate text-[10px] font-semibold text-gray-950 dark:text-white">{row.job}</p>
                    <p className="text-[8px] text-gray-400">{row.date}</p>
                  </div>
                </div>
                <p className="truncate text-[9px] text-gray-600 dark:text-gray-400">{row.company}</p>
                <p className="text-[9px] font-medium text-gray-700 dark:text-gray-300">{row.action}</p>
                <span className={`w-fit rounded-full px-1.5 py-0.5 text-[8px] font-semibold ${outcomeClass(row.outcome)}`}>{row.outcome}</span>
                <p className="text-right text-[9px] font-bold text-emerald-600 dark:text-emerald-400">{row.score}</p>
              </div>
            ))}
          </div>
        </MiniCard>
      </div>
    </div>
  );
}

function CompanionPreview({ isStatic = false, isActive = true }: { isStatic?: boolean; isActive?: boolean }) {
  const HERO_PREVIEW = useHeroPreviewCopy();
  type CompanionMessage = { id: string; role: 'user' | 'assistant'; text: string };

  const script: CompanionMessage[] = useMemo(
    () => [
      { id: 'm1', role: 'assistant', text: HERO_PREVIEW.companionGreeting },
      { id: 'm2', role: 'assistant', text: HERO_PREVIEW.companionReply },
      { id: 'm3', role: 'user', text: HERO_PREVIEW.companionUserAskMatches },
      { id: 'm4', role: 'assistant', text: HERO_PREVIEW.companionAssistantMatches },
      { id: 'm5', role: 'user', text: HERO_PREVIEW.companionUserAskOutreach },
      { id: 'm6', role: 'assistant', text: HERO_PREVIEW.companionAssistantOutreach },
    ],
    [HERO_PREVIEW],
  );

  const seedCount = 2;
  const [visibleCount, setVisibleCount] = useState(isStatic ? script.length : seedCount);
  const [composerText, setComposerText] = useState('');
  const [streamingText, setStreamingText] = useState('');
  const [isThinking, setIsThinking] = useState(false);
  const [simStep, setSimStep] = useState<'idle' | 'typing-user' | 'thinking' | 'typing-assistant' | 'hold'>('idle');

  const resetSimulation = () => {
    setVisibleCount(seedCount);
    setComposerText('');
    setStreamingText('');
    setIsThinking(false);
    setSimStep('idle');
  };

  useEffect(() => {
    if (isStatic) {
      setVisibleCount(script.length);
      setComposerText('');
      setStreamingText('');
      setIsThinking(false);
      setSimStep('idle');
      return;
    }

    if (!isActive) {
      resetSimulation();
      return;
    }

    resetSimulation();
    const startId = window.setTimeout(() => setSimStep('typing-user'), 900);
    return () => window.clearTimeout(startId);
  }, [isActive, isStatic, script.length]);

  useEffect(() => {
    if (isStatic || !isActive) return;

    const nextMessage = script[visibleCount];
    if (!nextMessage) return;

    if (simStep === 'typing-user' && nextMessage.role === 'user') {
      if (composerText.length < nextMessage.text.length) {
        const id = window.setTimeout(() => {
          setComposerText(nextMessage.text.slice(0, composerText.length + 1));
        }, COMPANION_TYPING_MS);
        return () => window.clearTimeout(id);
      }

      const id = window.setTimeout(() => {
        setVisibleCount((count) => count + 1);
        setComposerText('');
        setSimStep('thinking');
        setIsThinking(true);
      }, COMPANION_TURN_PAUSE_MS);
      return () => window.clearTimeout(id);
    }

    if (simStep === 'thinking') {
      const id = window.setTimeout(() => {
        setIsThinking(false);
        setSimStep('typing-assistant');
        setStreamingText('');
      }, COMPANION_THINK_MS);
      return () => window.clearTimeout(id);
    }

    if (simStep === 'typing-assistant' && nextMessage.role === 'assistant') {
      if (streamingText.length < nextMessage.text.length) {
        const id = window.setTimeout(() => {
          setStreamingText(nextMessage.text.slice(0, streamingText.length + 1));
        }, COMPANION_TYPING_MS);
        return () => window.clearTimeout(id);
      }

      const id = window.setTimeout(() => {
        setVisibleCount((count) => count + 1);
        setStreamingText('');
        if (visibleCount + 1 >= script.length) {
          setSimStep('hold');
        } else {
          setSimStep('hold');
        }
      }, COMPANION_TURN_PAUSE_MS);
      return () => window.clearTimeout(id);
    }

    if (simStep === 'hold') {
      const id = window.setTimeout(() => {
        if (visibleCount >= script.length) {
          resetSimulation();
          window.setTimeout(() => setSimStep('typing-user'), 700);
          return;
        }
        setSimStep('typing-user');
      }, visibleCount >= script.length ? COMPANION_LOOP_PAUSE_MS : COMPANION_TURN_PAUSE_MS);
      return () => window.clearTimeout(id);
    }

    return undefined;
  }, [composerText.length, isActive, isStatic, script, simStep, streamingText.length, visibleCount]);

  const renderedMessages = script.slice(0, visibleCount);
  const nextMessage = script[visibleCount];
  const assistantBusy = isThinking || (simStep === 'typing-assistant' && nextMessage?.role === 'assistant');
  const composerValue = isStatic ? '' : composerText;
  const showSendActive = !isStatic && simStep === 'typing-user' && composerText.length > 0;

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden">
      <PreviewHeader icon={MessageCircle} title={HERO_PREVIEW.companionTitle} live />
      <p className="shrink-0 border-b border-gray-50 px-3 pb-2 text-[10px] leading-snug text-gray-500 dark:border-gray-800 dark:text-gray-400">
        {HERO_PREVIEW.companionSubtitle}
      </p>

      <div className="grid min-h-0 flex-1 grid-cols-12 gap-1.5 overflow-hidden p-2">
        {/* Coach panel */}
        <MiniCard className="home-hero-preview-companion-coach col-span-4 flex min-h-0 flex-col overflow-hidden p-1.5">
          <div className="mb-1.5 flex items-center gap-1.5 rounded-md border border-gray-100 bg-gray-50/80 px-1.5 py-1 dark:border-gray-800 dark:bg-gray-900/40">
            <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-indigo-600 text-white">
              <Volume2 className="h-3 w-3" />
            </div>
            <span className="min-w-0 flex-1 truncate text-[9px] font-semibold text-gray-900 dark:text-white">
              {HERO_PREVIEW.companionCoachName}
            </span>
          </div>

          <div className="home-hero-preview-companion-avatar relative min-h-0 flex-1 overflow-hidden rounded-lg">
            <div className="absolute inset-0 bg-gradient-to-br from-indigo-100 via-violet-50 to-sky-100 dark:from-indigo-950/50 dark:via-violet-950/30 dark:to-sky-950/20" />
            <div className="absolute inset-0 flex items-center justify-center">
              <div
                className={`absolute h-14 w-14 rounded-full bg-indigo-500/10 ${assistantBusy ? 'animate-ping' : 'animate-pulse'}`}
                style={{ animationDuration: '2s' }}
              />
              <div
                className={`absolute h-10 w-10 rounded-full bg-indigo-500/15 ${assistantBusy ? 'animate-ping' : 'animate-pulse'}`}
                style={{ animationDuration: '1.5s' }}
              />
              <div className="relative flex h-12 w-12 items-center justify-center rounded-full border-2 border-white/80 bg-gradient-to-br from-indigo-500 to-violet-600 text-sm font-bold text-white shadow-md dark:border-white/20">
                {HERO_PREVIEW.companionCoachInitials}
              </div>
            </div>

            {assistantBusy ? (
              <div className="absolute inset-x-0 top-2 flex justify-center gap-0.5">
                {[0, 100, 200, 300, 400].map((delay) => (
                  <span
                    key={delay}
                    className="w-0.5 rounded-full bg-indigo-600/80 animate-bounce dark:bg-indigo-300"
                    style={{ height: `${8 + (delay % 3) * 4}px`, animationDelay: `${delay}ms` }}
                  />
                ))}
              </div>
            ) : null}

            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-white/95 to-transparent px-2 py-2 text-center dark:from-[#0f0f11]/95">
              <p className="text-[9px] font-bold text-gray-950 dark:text-white">{HERO_PREVIEW.companionCoachName}</p>
              <p className="text-[8px] text-gray-500 dark:text-gray-400">
                {assistantBusy ? HERO_PREVIEW.companionThinking : HERO_PREVIEW.companionCoachHint}
              </p>
            </div>

            <button
              type="button"
              className="absolute bottom-8 left-1/2 flex h-6 w-6 -translate-x-1/2 items-center justify-center rounded-full border border-indigo-200 bg-white text-indigo-600 shadow-sm dark:border-indigo-800 dark:bg-gray-900 dark:text-indigo-300"
              aria-label="Listen"
            >
              <Volume2 className="h-3 w-3" />
            </button>
          </div>

          <div className="mt-1.5 shrink-0 rounded-md border border-gray-100 bg-gray-50/80 px-1.5 py-1 text-[8px] text-gray-500 dark:border-gray-800 dark:bg-gray-900/40 dark:text-gray-400">
            {HERO_PREVIEW.companionVoicePrefix} {HERO_PREVIEW.companionVoice}
          </div>
        </MiniCard>

        {/* Chat panel */}
        <MiniCard className="home-hero-preview-companion-chat col-span-8 flex min-h-0 flex-col overflow-hidden">
          <div className="min-h-0 flex-1 space-y-1.5 overflow-hidden p-1.5">
            <AnimatePresence initial={false}>
              {renderedMessages.map((message) => (
                <motion.div
                  key={message.id}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.22 }}
                  className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`home-hero-preview-companion-bubble max-w-[88%] ${
                      message.role === 'user'
                        ? 'home-hero-preview-companion-bubble--user'
                        : 'home-hero-preview-companion-bubble--assistant'
                    }`}
                  >
                    <div className="flex items-start gap-1">
                      <p className="min-w-0 flex-1 text-[10px] leading-relaxed">{message.text}</p>
                      {message.role === 'assistant' ? (
                        <Volume2 className="mt-0.5 h-3 w-3 shrink-0 text-indigo-500 dark:text-indigo-300" />
                      ) : null}
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>

            {isThinking ? (
              <motion.div initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} className="flex justify-start">
                <div className="home-hero-preview-companion-bubble home-hero-preview-companion-bubble--assistant flex items-center gap-1.5">
                  <span className="flex gap-0.5">
                    {[0, 150, 300].map((delay) => (
                      <span
                        key={delay}
                        className="h-1.5 w-1.5 rounded-full bg-indigo-500 animate-bounce dark:bg-indigo-300"
                        style={{ animationDelay: `${delay}ms` }}
                      />
                    ))}
                  </span>
                  <span className="text-[9px] text-gray-500 dark:text-gray-400">{HERO_PREVIEW.companionThinking}</span>
                </div>
              </motion.div>
            ) : null}

            {simStep === 'typing-assistant' && nextMessage?.role === 'assistant' && streamingText ? (
              <motion.div initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} className="flex justify-start">
                <div className="home-hero-preview-companion-bubble home-hero-preview-companion-bubble--assistant max-w-[88%]">
                  <p className="text-[10px] leading-relaxed">
                    {streamingText}
                    <span className="ml-0.5 inline-block h-3 w-[2px] animate-pulse bg-indigo-500 align-middle dark:bg-indigo-300" />
                  </p>
                </div>
              </motion.div>
            ) : null}
          </div>

          <div className="shrink-0 border-t border-gray-100 p-1.5 dark:border-gray-800">
            <div className="flex items-end gap-1">
              <div className="home-hero-preview-companion-input min-h-[28px] flex-1 px-2 py-1.5 text-[10px] leading-snug text-gray-700 dark:text-gray-200">
                {composerValue || (
                  <span className="text-gray-400">{HERO_PREVIEW.companionInputPlaceholder}</span>
                )}
                {!isStatic && simStep === 'typing-user' && composerValue ? (
                  <span className="ml-0.5 inline-block h-3 w-[2px] animate-pulse bg-gray-900 align-middle dark:bg-white" />
                ) : null}
              </div>
              <button
                type="button"
                data-preview-target="companion-send"
                className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg ${
                  showSendActive ? 'bg-indigo-600 text-white' : 'border border-gray-200 bg-gray-50 text-gray-400 dark:border-gray-700 dark:bg-gray-900/40'
                }`}
                aria-label="Send message"
              >
                <Send className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border border-gray-200 bg-gray-50 text-gray-500 dark:border-gray-700 dark:bg-gray-900/40 dark:text-gray-400"
                aria-label="Speak"
              >
                <Mic className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        </MiniCard>
      </div>
    </div>
  );
}

function PreparePreview() {
  const HERO_PREVIEW = useHeroPreviewCopy();
  const [activeJob, setActiveJob] = useState(0);
  const jobs = [
    { title: HERO_PREVIEW.prepareJobOne, company: 'Stripe · Remote', topics: 12, progress: 80 },
    { title: HERO_PREVIEW.prepareJobTwo, company: 'Microsoft · Remote', topics: 9, progress: 45 },
    { title: HERO_PREVIEW.prepareJobThree, company: 'Acme Corp · Hybrid', topics: 7, progress: 20 },
  ];
  const scores = [
    { label: HERO_PREVIEW.prepareScoreClarity, value: 82, color: 'bg-indigo-500' },
    { label: HERO_PREVIEW.prepareScoreStructure, value: 76, color: 'bg-violet-500' },
    { label: HERO_PREVIEW.prepareScoreRoleFit, value: 88, color: 'bg-emerald-500' },
  ];
  const selected = jobs[activeJob];

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden">
      <PreviewHeader icon={Video} title={HERO_PREVIEW.prepareTitle} />
      <p className="shrink-0 border-b border-gray-50 px-3 pb-2 text-[10px] leading-snug text-gray-500 dark:border-gray-800 dark:text-gray-400">
        {HERO_PREVIEW.prepareSubtitle}
      </p>

      <div className="grid min-h-0 flex-1 grid-cols-12 gap-1.5 overflow-hidden p-2">
        <div className="col-span-5 flex min-h-0 flex-col gap-1.5 overflow-hidden">
          <SectionLabel>{HERO_PREVIEW.prepareJobsLabel}</SectionLabel>
          <div className="min-h-0 flex-1 space-y-1 overflow-hidden">
            {jobs.map((job, index) => (
              <MiniCard
                key={job.title}
                previewTarget={index === 0 ? 'prepare-job' : undefined}
                className={`cursor-pointer p-1.5 transition-colors ${activeJob === index ? 'border-indigo-200 bg-indigo-50/50 dark:border-indigo-800 dark:bg-indigo-950/20' : ''}`}
                onClick={() => setActiveJob(index)}
              >
                <div className="flex items-start gap-1.5">
                  <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-purple-50 text-purple-600 dark:bg-purple-950/40 dark:text-purple-300">
                    <Video className="h-3.5 w-3.5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="line-clamp-1 text-[10px] font-semibold text-gray-950 dark:text-white">{job.title}</p>
                    <p className="text-[9px] text-gray-500 dark:text-gray-400">{job.company}</p>
                    <div className="mt-1 flex items-center gap-1.5">
                      <div className="h-1 flex-1 overflow-hidden rounded-full bg-gray-100 dark:bg-gray-800">
                        <div className="h-full rounded-full bg-indigo-500" style={{ width: `${job.progress}%` }} />
                      </div>
                      <span className="text-[8px] font-semibold text-gray-400">{job.progress}%</span>
                    </div>
                  </div>
                </div>
              </MiniCard>
            ))}
          </div>
          <MiniCard className="shrink-0 p-1.5">
            <p className="text-[10px] font-semibold text-gray-950 dark:text-white">{HERO_PREVIEW.prepareUpcoming}</p>
            <p className="mt-0.5 text-[9px] text-gray-500 dark:text-gray-400">{HERO_PREVIEW.prepareUpcomingBody}</p>
          </MiniCard>
        </div>

        <MiniCard className="col-span-7 flex min-h-0 flex-col overflow-hidden">
          <div className="flex shrink-0 items-center justify-between border-b border-gray-100 px-2 py-1.5 dark:border-gray-800">
            <div className="flex items-center gap-1.5">
              <Play className="h-3.5 w-3.5 text-indigo-600 dark:text-indigo-400" />
              <span className="text-[10px] font-semibold text-gray-950 dark:text-white">{HERO_PREVIEW.prepareActiveSession}</span>
            </div>
            <span className="rounded-full bg-purple-50 px-1.5 py-0.5 text-[8px] font-semibold text-purple-700 dark:bg-purple-950/40 dark:text-purple-300">
              {HERO_PREVIEW.prepareCategory}
            </span>
          </div>

          <div className="min-h-0 flex-1 space-y-1.5 overflow-hidden p-1.5">
            <div className="rounded-md border border-gray-100 bg-gray-50/80 p-1.5 dark:border-gray-800 dark:bg-gray-900/40">
              <p className="text-[8px] font-bold uppercase tracking-wide text-gray-400">Question 3 of 12</p>
              <p className="mt-1 text-[10px] font-medium leading-snug text-gray-900 dark:text-white">{HERO_PREVIEW.prepareQuestion}</p>
            </div>

            <div className="grid grid-cols-3 gap-1">
              {[
                { label: HERO_PREVIEW.prepareMock, icon: Video, progress: selected.progress },
                { label: HERO_PREVIEW.prepareQuestions, icon: MessageCircle, progress: 45 },
                { label: HERO_PREVIEW.prepareFeedback, icon: Mic, progress: 62 },
              ].map(({ label, icon: Icon, progress }) => (
                <div key={label} className="rounded-md border border-gray-100 bg-white p-1 dark:border-gray-800 dark:bg-gray-900/30">
                  <Icon className="mb-0.5 h-3 w-3 text-indigo-600 dark:text-indigo-400" />
                  <p className="line-clamp-1 text-[8px] font-semibold text-gray-800 dark:text-gray-200">{label}</p>
                  <div className="mt-1 h-1 overflow-hidden rounded-full bg-gray-100 dark:bg-gray-800">
                    <div className="h-full rounded-full bg-indigo-500" style={{ width: `${progress}%` }} />
                  </div>
                </div>
              ))}
            </div>

            <div className="space-y-1">
              <p className="text-[8px] font-bold uppercase tracking-wide text-gray-400">AI feedback scores</p>
              {scores.map((score) => (
                <div key={score.label} className="flex items-center gap-1.5">
                  <span className="w-12 shrink-0 text-[9px] text-gray-500 dark:text-gray-400">{score.label}</span>
                  <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-gray-100 dark:bg-gray-800">
                    <div className={`h-full rounded-full ${score.color}`} style={{ width: `${score.value}%` }} />
                  </div>
                  <span className="w-6 shrink-0 text-right text-[9px] font-bold text-gray-700 dark:text-gray-200">{score.value}</span>
                </div>
              ))}
            </div>

            <div className="home-hero-preview-prepare-video relative overflow-hidden rounded-md border border-gray-100 dark:border-gray-800">
              <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-indigo-950 to-violet-950" />
              <div className="relative flex aspect-[16/7] items-center justify-center">
                <button type="button" className="flex h-8 w-8 items-center justify-center rounded-full bg-white/15 text-white backdrop-blur-sm">
                  <Play className="h-4 w-4 fill-white" />
                </button>
              </div>
              <div className="relative border-t border-white/10 px-2 py-1">
                <p className="line-clamp-1 text-[9px] font-semibold text-white">{selected.title} · curated prep</p>
                <p className="text-[8px] text-white/60">{selected.topics} topics · {selected.company}</p>
              </div>
            </div>
          </div>

          <div className="shrink-0 border-t border-gray-100 p-1.5 dark:border-gray-800">
            <button type="button" className="flex w-full items-center justify-center gap-1 rounded-md bg-indigo-600 px-2 py-1.5 text-[10px] font-semibold text-white">
              <Video className="h-3.5 w-3.5" />
              {HERO_PREVIEW.prepareStart}
            </button>
          </div>
        </MiniCard>
      </div>
    </div>
  );
}

function CalendarPreview() {
  const HERO_PREVIEW = useHeroPreviewCopy();
  const previewMonth = { year: 2026, month: 4 };
  const today = 28;
  const [selectedDay, setSelectedDay] = useState(today);

  const daysInMonth = new Date(previewMonth.year, previewMonth.month + 1, 0).getDate();
  const firstWeekday = new Date(previewMonth.year, previewMonth.month, 1).getDay();
  const weekdayLabels = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

  type PreviewCalendarEntry = {
    id: string;
    kind: 'application' | 'optimization' | 'reminder' | 'interview';
    title: string;
    subtitle: string;
    meta: string;
    badge?: string;
  };

  const entriesByDay: Record<number, PreviewCalendarEntry[]> = {
    12: [
      {
        id: 'apply-12',
        kind: 'application',
        title: 'Applied · Lead PM',
        subtitle: 'Microsoft · Remote',
        meta: '10:30 AM',
        badge: 'Applied',
      },
    ],
    15: [
      {
        id: 'opt-15',
        kind: 'optimization',
        title: 'Resume optimized',
        subtitle: 'Senior PM · Stripe',
        meta: '2:15 PM',
        badge: 'Optimized',
      },
    ],
    19: [
      {
        id: 'int-19',
        kind: 'interview',
        title: HERO_PREVIEW.calendarInterview,
        subtitle: 'Thu 3:00 PM · Video call',
        meta: '3:00 PM',
        badge: 'Interview',
      },
    ],
    23: [
      {
        id: 'rem-23',
        kind: 'reminder',
        title: HERO_PREVIEW.calendarFollowUp,
        subtitle: 'Mon 10:00 AM · Email reminder',
        meta: '10:00 AM',
        badge: 'Reminder',
      },
    ],
    26: [
      {
        id: 'rem-26',
        kind: 'reminder',
        title: 'Send thank-you note',
        subtitle: 'Acme Corp interview follow-up',
        meta: '9:00 AM',
        badge: 'Reminder',
      },
    ],
    28: [
      {
        id: 'rem-28',
        kind: 'reminder',
        title: 'Research company metrics',
        subtitle: 'Before Thursday interview',
        meta: 'Today',
        badge: 'Reminder',
      },
      {
        id: 'opt-28',
        kind: 'optimization',
        title: 'Tailor resume · Stripe',
        subtitle: 'AI optimization queued',
        meta: '11:00 AM',
        badge: 'Optimized',
      },
    ],
  };

  const notes = [
    { id: 'n1', text: HERO_PREVIEW.calendarNoteOne, time: 'May 27, 4:20 PM' },
    { id: 'n2', text: HERO_PREVIEW.calendarNoteTwo, time: 'May 24, 9:05 AM' },
  ];

  const selectedDateLabel = useMemo(() => {
    const date = new Date(previewMonth.year, previewMonth.month, selectedDay);
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  }, [selectedDay]);

  const selectedEntries = entriesByDay[selectedDay] ?? [];

  const entryStyles: Record<
    PreviewCalendarEntry['kind'],
    { border: string; iconBg: string; iconColor: string; Icon: React.ComponentType<{ className?: string }> }
  > = {
    application: { border: 'border-l-emerald-500', iconBg: 'bg-emerald-500/10', iconColor: 'text-emerald-600', Icon: Briefcase },
    optimization: { border: 'border-l-indigo-500', iconBg: 'bg-indigo-500/10', iconColor: 'text-indigo-600', Icon: Sparkles },
    reminder: { border: 'border-l-amber-500', iconBg: 'bg-amber-500/10', iconColor: 'text-amber-600', Icon: Bell },
    interview: { border: 'border-l-violet-500', iconBg: 'bg-violet-500/10', iconColor: 'text-violet-600', Icon: Video },
  };

  const dotColors: Record<PreviewCalendarEntry['kind'], string> = {
    application: 'bg-emerald-500',
    optimization: 'bg-indigo-500',
    reminder: 'bg-amber-500',
    interview: 'bg-violet-500',
  };

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden">
      <PreviewHeader icon={CalendarDays} title={HERO_PREVIEW.calendarTitle} />
      <p className="shrink-0 border-b border-gray-50 px-3 pb-2 text-[10px] leading-snug text-gray-500 dark:border-gray-800 dark:text-gray-400">
        {HERO_PREVIEW.calendarSubtitle}
      </p>

      <div className="grid min-h-0 flex-1 grid-cols-12 gap-1.5 overflow-hidden p-2">
        {/* Calendar card */}
        <MiniCard className="home-hero-preview-calendar col-span-5 flex min-h-0 flex-col overflow-hidden">
          <div className="flex shrink-0 items-center justify-between border-b border-gray-100 px-2 py-1.5 dark:border-gray-800">
            <div className="flex min-w-0 items-center gap-1.5">
              <CalendarDays className="h-3.5 w-3.5 shrink-0 text-indigo-600 dark:text-indigo-400" />
              <span className="truncate text-[10px] font-semibold text-gray-950 dark:text-white">{HERO_PREVIEW.calendarMonth}</span>
            </div>
            <div className="flex shrink-0 gap-0.5">
              <button type="button" className="home-hero-preview-calendar-nav" aria-label="Previous month">
                <ChevronLeft className="h-3.5 w-3.5" />
              </button>
              <button type="button" className="home-hero-preview-calendar-nav" aria-label="Next month">
                <ChevronRight className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-hidden p-1.5">
            <div className="grid grid-cols-7 gap-0.5 text-center">
              {weekdayLabels.map((label) => (
                <span key={label} className="py-0.5 text-[9px] font-semibold text-gray-400">
                  {label}
                </span>
              ))}
              {Array.from({ length: firstWeekday }, (_, index) => (
                <span key={`blank-${index}`} className="home-hero-preview-calendar-day home-hero-preview-calendar-day--empty" aria-hidden />
              ))}
              {Array.from({ length: daysInMonth }, (_, index) => {
                const day = index + 1;
                const dayEntries = entriesByDay[day] ?? [];
                const isSelected = selectedDay === day;
                const isToday = day === today;
                const uniqueKinds = [...new Set(dayEntries.map((entry) => entry.kind))];

                return (
                  <button
                    key={day}
                    type="button"
                    onClick={() => setSelectedDay(day)}
                    className={`home-hero-preview-calendar-day ${isSelected ? 'home-hero-preview-calendar-day--selected' : ''} ${
                      isToday ? 'home-hero-preview-calendar-day--today' : ''
                    }`}
                    data-preview-target={day === 19 ? 'calendar-day' : undefined}
                    aria-label={`Select May ${day}`}
                    aria-pressed={isSelected}
                  >
                    <span className="text-[10px] font-semibold leading-none">{day}</span>
                    {uniqueKinds.length > 0 ? (
                      <span className="mt-0.5 flex gap-0.5" aria-hidden>
                        {uniqueKinds.slice(0, 3).map((kind) => (
                          <span key={kind} className={`h-1 w-1 rounded-full ${dotColors[kind]}`} />
                        ))}
                      </span>
                    ) : null}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex shrink-0 flex-wrap items-center gap-2 border-t border-gray-100 px-2 py-1.5 dark:border-gray-800">
            {[
              { label: 'Applied', color: 'bg-emerald-500' },
              { label: 'Optimized', color: 'bg-indigo-500' },
              { label: 'Reminder', color: 'bg-amber-500' },
              { label: 'Interview', color: 'bg-violet-500' },
            ].map((item) => (
              <span key={item.label} className="inline-flex items-center gap-1 text-[8px] text-gray-500 dark:text-gray-400">
                <span className={`h-1.5 w-1.5 rounded-full ${item.color}`} />
                {item.label}
              </span>
            ))}
          </div>
        </MiniCard>

        {/* Entries + notes */}
        <div className="col-span-7 flex min-h-0 flex-col gap-1.5 overflow-hidden">
          <MiniCard className="flex min-h-0 flex-[1.15] flex-col overflow-hidden">
            <div className="flex shrink-0 items-center gap-1.5 border-b border-gray-100 px-2 py-1.5 dark:border-gray-800">
              <ListTodo className="h-3.5 w-3.5 shrink-0 text-indigo-600 dark:text-indigo-400" />
              <span className="min-w-0 flex-1 truncate text-[10px] font-semibold text-gray-950 dark:text-white">
                {HERO_PREVIEW.calendarEntries} · {selectedDateLabel}
              </span>
              <button
                type="button"
                className="flex h-5 w-5 shrink-0 items-center justify-center rounded-md bg-indigo-50 text-indigo-600 dark:bg-indigo-950/40 dark:text-indigo-300"
                aria-label="Add entry"
              >
                <Plus className="h-3 w-3" />
              </button>
            </div>

            <div className="min-h-0 flex-1 space-y-1 overflow-hidden p-1.5">
              {selectedEntries.length === 0 ? (
                <div className="flex h-full items-center justify-center rounded-md border border-dashed border-gray-200 bg-gray-50/80 px-2 py-4 text-center text-[10px] text-gray-400 dark:border-gray-700 dark:bg-gray-900/40">
                  {HERO_PREVIEW.calendarNoEntries}
                </div>
              ) : (
                selectedEntries.map((entry) => {
                  const style = entryStyles[entry.kind];
                  const EntryIcon = style.Icon;
                  return (
                    <div
                      key={entry.id}
                      className={`home-hero-preview-calendar-entry flex gap-1.5 border-l-[3px] ${style.border} rounded-md border border-gray-100 bg-gray-50/70 p-1.5 dark:border-gray-800 dark:bg-gray-900/40`}
                    >
                      <div className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-md ${style.iconBg}`}>
                        <EntryIcon className={`h-3 w-3 ${style.iconColor}`} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-start justify-between gap-1">
                          <p className="line-clamp-1 text-[10px] font-semibold text-gray-950 dark:text-white">{entry.title}</p>
                          {entry.badge ? (
                            <span className="shrink-0 rounded bg-white px-1 py-0.5 text-[8px] font-medium text-gray-600 dark:bg-gray-950 dark:text-gray-300">
                              {entry.badge}
                            </span>
                          ) : null}
                        </div>
                        <p className="mt-0.5 line-clamp-1 text-[9px] text-gray-500 dark:text-gray-400">{entry.subtitle}</p>
                        <div className="mt-0.5 flex items-center gap-1 text-[8px] text-gray-400">
                          <Clock className="h-2.5 w-2.5 shrink-0" />
                          {entry.meta}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </MiniCard>

          <MiniCard className="flex min-h-0 flex-1 flex-col overflow-hidden">
            <div className="flex shrink-0 items-center gap-1.5 border-b border-gray-100 px-2 py-1.5 dark:border-gray-800">
              <StickyNote className="h-3.5 w-3.5 shrink-0 text-indigo-600 dark:text-indigo-400" />
              <span className="text-[10px] font-semibold text-gray-950 dark:text-white">{HERO_PREVIEW.calendarNotes}</span>
            </div>

            <div className="flex shrink-0 gap-1 border-b border-gray-100 p-1.5 dark:border-gray-800">
              <div className="min-w-0 flex-1 rounded-md border border-gray-200 bg-gray-50 px-2 py-1 text-[10px] text-gray-400 dark:border-gray-700 dark:bg-gray-900/40">
                {HERO_PREVIEW.calendarAddNotePlaceholder}
              </div>
              <button
                type="button"
                className="inline-flex shrink-0 items-center gap-1 rounded-md bg-indigo-600 px-2 py-1 text-[9px] font-semibold text-white"
              >
                <Plus className="h-3 w-3" />
                Add
              </button>
            </div>

            <div className="min-h-0 flex-1 space-y-1 overflow-hidden p-1.5">
              {notes.map((note) => (
                <div
                  key={note.id}
                  className="rounded-md border border-gray-100 bg-gray-50/80 px-2 py-1.5 dark:border-gray-800 dark:bg-gray-900/40"
                >
                  <p className="line-clamp-2 text-[10px] leading-snug text-gray-700 dark:text-gray-300">{note.text}</p>
                  <p className="mt-0.5 text-[8px] text-gray-400">{note.time}</p>
                </div>
              ))}
            </div>
          </MiniCard>
        </div>
      </div>
    </div>
  );
}

function ReferralsPreview() {
  const HERO_PREVIEW = useHeroPreviewCopy();
  const referrals = [
    { name: 'Alex Kumar', meta: HERO_PREVIEW.referralsSampleOneMeta, status: HERO_PREVIEW.referralsJoined, tone: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300' },
    { name: 'Priya Shah', meta: HERO_PREVIEW.referralsSampleTwoMeta, status: HERO_PREVIEW.referralsPending, tone: 'bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300' },
    { name: 'Rohan Mehta', meta: HERO_PREVIEW.referralsSampleThreeMeta, status: HERO_PREVIEW.referralsPending, tone: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300' },
  ];

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden">
      <PreviewHeader icon={Gift} title={HERO_PREVIEW.referralsTitle} />
      <p className="shrink-0 border-b border-gray-50 px-3 pb-2 text-[10px] leading-snug text-gray-500 dark:border-gray-800 dark:text-gray-400">
        {HERO_PREVIEW.referralsSubtitle}
      </p>

      <div className="grid min-h-0 flex-1 grid-cols-12 gap-1.5 overflow-hidden p-2">
        <MiniCard className="home-hero-preview-referrals-hero col-span-7 flex min-h-0 flex-col overflow-hidden bg-gradient-to-br from-indigo-50/90 to-emerald-50/50 p-2 dark:from-indigo-950/30 dark:to-emerald-950/20">
          <div className="flex items-start gap-2">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-indigo-600 text-white">
              <Gift className="h-4 w-4" />
            </div>
            <div className="min-w-0">
              <p className="text-[11px] font-bold text-gray-950 dark:text-white">{HERO_PREVIEW.referralsTitle}</p>
              <p className="mt-0.5 text-[10px] leading-snug text-gray-600 dark:text-gray-400">{HERO_PREVIEW.referralsBody}</p>
            </div>
          </div>

          <div className="mt-2 grid grid-cols-2 gap-1.5">
            <div className="rounded-md border border-white/70 bg-white/70 px-2 py-1.5 dark:border-gray-800 dark:bg-gray-900/40">
              <p className="text-[8px] font-bold uppercase tracking-wide text-gray-400">{HERO_PREVIEW.referralsCreditsEarned}</p>
              <p className="text-base font-extrabold leading-none text-emerald-600 dark:text-emerald-400">20</p>
            </div>
            <div className="rounded-md border border-white/70 bg-white/70 px-2 py-1.5 dark:border-gray-800 dark:bg-gray-900/40">
              <p className="text-[8px] font-bold uppercase tracking-wide text-gray-400">{HERO_PREVIEW.referralsFriendsJoined}</p>
              <p className="text-base font-extrabold leading-none text-indigo-600 dark:text-indigo-400">2</p>
            </div>
          </div>

          <div className="mt-2 flex min-w-0 items-center gap-1 rounded-md border border-indigo-100 bg-white/80 px-1.5 py-1 dark:border-indigo-900/40 dark:bg-gray-900/50">
            <span className="min-w-0 flex-1 truncate text-[9px] font-medium text-gray-700 dark:text-gray-300">{HERO_PREVIEW.referralsInviteLink}</span>
            <button type="button" data-preview-target="referrals-copy" className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-indigo-50 text-indigo-600 dark:bg-indigo-950/40 dark:text-indigo-300">
              <Copy className="h-3 w-3" />
            </button>
          </div>

          <button type="button" className="mt-2 inline-flex w-full items-center justify-center gap-1 rounded-md bg-indigo-600 px-2 py-1.5 text-[10px] font-semibold text-white">
            <Gift className="h-3.5 w-3.5" />
            {HERO_PREVIEW.referralsCta}
          </button>

          <div className="mt-auto rounded-md border border-emerald-100 bg-emerald-50/70 px-2 py-1.5 dark:border-emerald-900/40 dark:bg-emerald-950/20">
            <div className="flex items-center gap-1 text-[9px] font-medium text-emerald-700 dark:text-emerald-300">
              <Award className="h-3 w-3 shrink-0" />
              {HERO_PREVIEW.referralsNextReward}
            </div>
            <div className="mt-1 h-1 overflow-hidden rounded-full bg-emerald-100 dark:bg-emerald-950/40">
              <div className="h-full w-2/5 rounded-full bg-emerald-500" />
            </div>
          </div>
        </MiniCard>

        <MiniCard className="col-span-5 flex min-h-0 flex-col overflow-hidden">
          <div className="shrink-0 border-b border-gray-100 px-2 py-1.5 dark:border-gray-800">
            <p className="text-[10px] font-semibold text-gray-950 dark:text-white">{HERO_PREVIEW.referralsRecent}</p>
            <p className="text-[9px] text-gray-500 dark:text-gray-400">{HERO_PREVIEW.referralsStat}</p>
          </div>
          <div className="min-h-0 flex-1 space-y-1 overflow-hidden p-1.5">
            {referrals.map((referral) => (
              <div key={referral.name} className="flex items-center gap-1.5 rounded-md border border-gray-100 bg-gray-50/70 p-1.5 dark:border-gray-800 dark:bg-gray-900/40">
                <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-indigo-100 text-[9px] font-bold text-indigo-600 dark:bg-indigo-950/40">
                  {referral.name.charAt(0)}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[10px] font-semibold text-gray-950 dark:text-white">{referral.name}</p>
                  <p className="truncate text-[8px] text-gray-500 dark:text-gray-400">{referral.meta}</p>
                </div>
                <span className={`shrink-0 rounded-full px-1.5 py-0.5 text-[8px] font-semibold ${referral.tone}`}>{referral.status}</span>
              </div>
            ))}
          </div>
        </MiniCard>
      </div>
    </div>
  );
}

function SupportPreview() {
  const HERO_PREVIEW = useHeroPreviewCopy();
  const [statusFilter, setStatusFilter] = useState(HERO_PREVIEW.supportFilterAll);
  const stats = [
    { label: HERO_PREVIEW.supportStatOpen, value: 2, tone: 'text-amber-600 dark:text-amber-400' },
    { label: HERO_PREVIEW.supportStatProgress, value: 1, tone: 'text-indigo-600 dark:text-indigo-400' },
    { label: HERO_PREVIEW.supportStatResolved, value: 5, tone: 'text-emerald-600 dark:text-emerald-400' },
  ];
  const tickets = [
    { subject: HERO_PREVIEW.supportOpen, status: HERO_PREVIEW.supportStatusOpen, priority: HERO_PREVIEW.supportPriorityHigh, statusClass: 'bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300', priorityClass: 'bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300', time: '2h ago', id: '#1042' },
    { subject: HERO_PREVIEW.supportTicketAgentVariant, status: HERO_PREVIEW.supportStatusOpen, priority: HERO_PREVIEW.supportPriorityMedium, statusClass: 'bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300', priorityClass: 'bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300', time: '3d ago', id: '#1038' },
    { subject: HERO_PREVIEW.supportResolved, status: HERO_PREVIEW.supportStatusResolved, priority: HERO_PREVIEW.supportPriorityMedium, statusClass: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300', priorityClass: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300', time: 'Yesterday', id: '#1031' },
    { subject: HERO_PREVIEW.supportTicketCreditsReferral, status: HERO_PREVIEW.supportStatusInProgress, priority: HERO_PREVIEW.supportPriorityMedium, statusClass: 'bg-indigo-50 text-indigo-700 dark:bg-indigo-950/40 dark:text-indigo-300', priorityClass: 'bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300', time: 'Mon', id: '#1029' },
  ];
  const filteredTickets = tickets.filter((ticket) => statusFilter === HERO_PREVIEW.supportFilterAll || ticket.status === statusFilter);

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden">
      <PreviewHeader icon={LifeBuoy} title={HERO_PREVIEW.supportTitle} />
      <p className="shrink-0 border-b border-gray-50 px-3 pb-2 text-[10px] leading-snug text-gray-500 dark:border-gray-800 dark:text-gray-400">
        {HERO_PREVIEW.supportSubtitle}
      </p>

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden p-2">
        <div className="mb-1 grid shrink-0 grid-cols-3 gap-1.5">
          {stats.map((stat) => (
            <MiniCard key={stat.label} className="px-2 py-1.5">
              <p className="text-[8px] font-bold uppercase tracking-wide text-gray-400">{stat.label}</p>
              <p className={`text-base font-extrabold leading-none ${stat.tone}`}>{stat.value}</p>
            </MiniCard>
          ))}
        </div>

        <div className="mb-1 flex shrink-0 items-center gap-1">
          <div className="flex min-w-0 flex-1 items-center gap-1 rounded-md border border-gray-200 bg-gray-50 px-1.5 py-1 dark:border-gray-700 dark:bg-gray-900/40">
            <Search className="h-3 w-3 shrink-0 text-gray-400" />
            <span className="truncate text-[9px] text-gray-400">{HERO_PREVIEW.supportSearchPlaceholder}</span>
          </div>
          {['All', 'Open', 'In progress', 'Resolved'].map((chip) => (
            <button
              key={chip}
              type="button"
              data-preview-target={chip === 'Open' ? 'support-filter' : undefined}
              onClick={() => setStatusFilter(chip)}
              className={`shrink-0 rounded-full border px-1.5 py-0.5 text-[8px] font-medium ${
                statusFilter === chip
                  ? 'border-indigo-200 bg-indigo-50 text-indigo-700 dark:border-indigo-800 dark:bg-indigo-950/40 dark:text-indigo-300'
                  : 'border-gray-200 bg-white text-gray-600 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300'
              }`}
            >
              {chip}
            </button>
          ))}
        </div>

        <MiniCard className="min-h-0 flex-1 overflow-hidden">
          <div className="grid shrink-0 grid-cols-[0.55fr_minmax(0,1fr)_0.65fr_0.55fr_0.45fr] gap-1 border-b border-gray-100 bg-gray-50/80 px-2 py-1 text-[8px] font-bold uppercase tracking-wide text-gray-400 dark:border-gray-800 dark:bg-gray-900/40">
            <span>ID</span>
            <span>Subject</span>
            <span>Status</span>
            <span>Priority</span>
            <span className="text-right">Updated</span>
          </div>
          <div className="min-h-0 overflow-hidden">
            {filteredTickets.map((ticket) => (
              <div
                key={ticket.id}
                className="grid grid-cols-[0.55fr_minmax(0,1fr)_0.65fr_0.55fr_0.45fr] gap-1 border-b border-gray-50 px-2 py-1.5 last:border-0 dark:border-gray-800/80"
              >
                <span className="text-[9px] font-semibold text-gray-500 dark:text-gray-400">{ticket.id}</span>
                <p className="truncate text-[10px] font-semibold text-gray-950 dark:text-white">{ticket.subject}</p>
                <span className={`w-fit rounded-full px-1.5 py-0.5 text-[8px] font-semibold ${ticket.statusClass}`}>{ticket.status}</span>
                <span className={`w-fit rounded-full px-1.5 py-0.5 text-[8px] font-semibold ${ticket.priorityClass}`}>{ticket.priority}</span>
                <span className="text-right text-[8px] text-gray-400">{ticket.time}</span>
              </div>
            ))}
          </div>
        </MiniCard>

        <button type="button" className="mt-1 shrink-0 rounded-md border border-dashed border-indigo-200 bg-indigo-50/50 py-1.5 text-[10px] font-semibold text-indigo-600 dark:border-indigo-800 dark:bg-indigo-950/20 dark:text-indigo-300">
          + {HERO_PREVIEW.supportNew}
        </button>
      </div>
    </div>
  );
}

function SettingsPreview() {
  const HERO_PREVIEW = useHeroPreviewCopy();
  const skillTags = ['Product strategy', 'Roadmapping', 'SQL', 'Stakeholder mgmt'];

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden">
      <PreviewHeader icon={Settings} title={HERO_PREVIEW.settingsTitle} />
      <div className="shrink-0 border-b border-gray-50 px-3 pb-2 dark:border-gray-800">
        <p className="flex items-center gap-1.5 text-[10px] font-semibold text-gray-950 dark:text-white">
          <User className="h-3.5 w-3.5 text-indigo-600 dark:text-indigo-400" />
          {HERO_PREVIEW.settingsPageTitle}
        </p>
        <p className="mt-0.5 text-[10px] leading-snug text-gray-500 dark:text-gray-400">{HERO_PREVIEW.settingsSubtitle}</p>
      </div>

      <div className="home-hero-preview-settings-scroll min-h-0 flex-1 overflow-y-auto overflow-x-hidden p-2">
        <PreviewSettingsSection title={HERO_PREVIEW.settingsProfileSection}>
          <dl className="space-y-0.5">
            <PreviewLabelRow label={HERO_PREVIEW.settingsPhoto}>
              <div className="flex justify-end">
                <div className="home-hero-preview-settings-avatar relative flex h-12 w-12 items-center justify-center rounded-full border-2 border-dashed border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-900/40">
                  <span className="text-[8px] font-bold text-indigo-600 dark:text-indigo-300">JD</span>
                  <span className="absolute -bottom-0.5 -right-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-indigo-600 text-white">
                    <Camera className="h-2.5 w-2.5" />
                  </span>
                </div>
              </div>
            </PreviewLabelRow>
            <PreviewLabelRow label={HERO_PREVIEW.settingsName}>
              <PreviewSettingsField value="Jane Doe" />
            </PreviewLabelRow>
            <PreviewLabelRow label={HERO_PREVIEW.settingsEmail} readOnly>
              <PreviewSettingsField value="jane.doe@email.com" readOnly />
            </PreviewLabelRow>
            <PreviewLabelRow label={HERO_PREVIEW.settingsPhone}>
              <PreviewSettingsField value="+91 98765 43210" />
            </PreviewLabelRow>
            <PreviewLabelRow label={HERO_PREVIEW.settingsGender}>
              <PreviewSettingsField value={HERO_PREVIEW.settingsGenderValue} />
            </PreviewLabelRow>
            <PreviewLabelRow label={HERO_PREVIEW.settingsStreet}>
              <PreviewSettingsField value="12 MG Road" />
            </PreviewLabelRow>
            <PreviewLabelRow label={HERO_PREVIEW.settingsCity}>
              <PreviewSettingsField value="Bengaluru" />
            </PreviewLabelRow>
            <PreviewLabelRow label={HERO_PREVIEW.settingsCountry}>
              <PreviewSettingsField value="India" />
            </PreviewLabelRow>
          </dl>
          <div className="mt-1.5 flex justify-end">
            <PreviewSettingsButton label={HERO_PREVIEW.settingsSaveProfile} icon={CheckCircle2} previewTarget="settings-save" />
          </div>
        </PreviewSettingsSection>

        <PreviewSettingsSection title={HERO_PREVIEW.settingsJobPreferences}>
          <div className="space-y-1">
            <div>
              <p className="mb-0.5 text-[9px] font-medium text-gray-500 dark:text-gray-400">{HERO_PREVIEW.settingsSkills}</p>
              <PreviewSettingsField value={HERO_PREVIEW.settingsSkillsPlaceholder} />
              <div className="mt-1 flex flex-wrap gap-0.5">
                {skillTags.map((skill) => (
                  <span key={skill} className="home-hero-preview-settings-skill inline-flex items-center gap-0.5 rounded px-1 py-0.5 text-[8px] font-medium">
                    <span className="truncate">{skill}</span>
                    <X className="h-2.5 w-2.5 shrink-0 opacity-60" />
                  </span>
                ))}
              </div>
              <p className="mt-0.5 text-[8px] text-gray-400">{HERO_PREVIEW.settingsSkillsHint}</p>
            </div>

            <div className="grid grid-cols-2 gap-1">
              <div>
                <p className="mb-0.5 text-[9px] font-medium text-gray-500 dark:text-gray-400">{HERO_PREVIEW.settingsDomain}</p>
                <PreviewSettingsField value="B2B SaaS" />
              </div>
              <div>
                <p className="mb-0.5 text-[9px] font-medium text-gray-500 dark:text-gray-400">{HERO_PREVIEW.settingsLocation}</p>
                <PreviewSettingsField value="Bengaluru / Remote" />
              </div>
              <div>
                <p className="mb-0.5 text-[9px] font-medium text-gray-500 dark:text-gray-400">{HERO_PREVIEW.settingsJobType}</p>
                <PreviewSettingsField value="Full-time" />
              </div>
              <div>
                <p className="mb-0.5 text-[9px] font-medium text-gray-500 dark:text-gray-400">{HERO_PREVIEW.settingsExperience}</p>
                <PreviewSettingsField value="Senior" />
              </div>
            </div>

            <label className="flex items-center gap-1.5 text-[9px] text-gray-700 dark:text-gray-300">
              <input type="checkbox" checked readOnly className="h-3 w-3 rounded border-gray-300" />
              {HERO_PREVIEW.settingsRemotePreferred}
            </label>
          </div>
          <div className="mt-1.5 flex justify-end">
            <PreviewSettingsButton label={HERO_PREVIEW.settingsSavePreferences} icon={CheckCircle2} />
          </div>
        </PreviewSettingsSection>

        <PreviewSettingsSection title={HERO_PREVIEW.settingsSecuritySection}>
          <dl className="space-y-0.5">
            <PreviewLabelRow label={HERO_PREVIEW.settingsCurrentPassword}>
              <PreviewSettingsField value="••••••••" masked />
            </PreviewLabelRow>
            <PreviewLabelRow label={HERO_PREVIEW.settingsNewPassword}>
              <PreviewSettingsField value="••••••••" masked />
            </PreviewLabelRow>
            <PreviewLabelRow label={HERO_PREVIEW.settingsConfirmPassword}>
              <PreviewSettingsField value="••••••••" masked />
            </PreviewLabelRow>
          </dl>
          <div className="mt-1.5 flex justify-end">
            <PreviewSettingsButton label={HERO_PREVIEW.settingsUpdatePassword} icon={Lock} />
          </div>
          <div className="mt-1 rounded-md border border-amber-100 bg-amber-50/70 px-1.5 py-1 dark:border-amber-900/40 dark:bg-amber-950/20">
            <p className="text-[8px] leading-snug text-amber-700 dark:text-amber-300">{HERO_PREVIEW.settingsPasswordNote}</p>
          </div>
        </PreviewSettingsSection>

        <PreviewSettingsSection title={HERO_PREVIEW.settingsDataPrivacy}>
          <div className="rounded-md border border-gray-100 bg-gray-50/70 p-1.5 dark:border-gray-800 dark:bg-gray-900/40">
            <p className="text-[10px] font-semibold text-gray-950 dark:text-white">{HERO_PREVIEW.settingsDeleteAccount}</p>
            <p className="mt-0.5 text-[9px] leading-snug text-gray-600 dark:text-gray-400">{HERO_PREVIEW.settingsDeleteAccountBody}</p>
            <div className="mt-1 rounded-md border border-amber-100 bg-amber-50/60 px-1.5 py-1 dark:border-amber-900/30 dark:bg-amber-950/15">
              <p className="text-[8px] leading-snug text-amber-700 dark:text-amber-300">
                <span className="font-semibold">{HERO_PREVIEW.settingsWarning}</span> {HERO_PREVIEW.settingsDeleteAccountWarning}
              </p>
            </div>
            <div className="mt-1.5 flex justify-end">
              <PreviewSettingsButton label={HERO_PREVIEW.settingsDeleteMyAccount} primary={false} />
            </div>
          </div>
        </PreviewSettingsSection>
      </div>
    </div>
  );
}

function MainPreview({
  activeView,
  jobs,
  agentProps,
  isStatic,
  activityFilter,
}: {
  activeView: PreviewViewId;
  jobs: MockJob[];
  agentProps: Omit<React.ComponentProps<typeof AgentPreview>, 'jobs'>;
  isStatic: boolean;
  activityFilter?: string;
}) {
  if (activeView === 'agent') {
    return (
      <div className="grid h-full min-h-0 flex-1 grid-cols-12 overflow-hidden">
        <AgentPreview jobs={jobs} {...agentProps} />
      </div>
    );
  }

  const panel = (() => {
    switch (activeView) {
      case 'home':
        return <DashboardPreview jobs={jobs} />;
      case 'jobs':
        return <JobsPreview jobs={jobs} />;
      case 'activity':
        return <ActivityPreview initialFilter={activityFilter} />;
      case 'companion':
        return <CompanionPreview isStatic={isStatic} isActive={activeView === 'companion'} />;
      case 'prepare':
        return <PreparePreview />;
      case 'calendar':
        return <CalendarPreview />;
      case 'referrals':
        return <ReferralsPreview />;
      case 'support':
        return <SupportPreview />;
      case 'settings':
        return <SettingsPreview />;
      default:
        return <DashboardPreview jobs={jobs} />;
    }
  })();

  return (
    <div data-preview-panel className="flex h-full min-h-0 flex-1 flex-col overflow-hidden bg-white dark:bg-[#0f0f11]">
      {panel}
    </div>
  );
}

export type HeroFeaturePreviewView = 'agent' | 'jobs' | 'home' | 'activity';

export function HeroFeaturePreviewPanel({
  view,
  activityFilter,
}: {
  view: HeroFeaturePreviewView;
  activityFilter?: string;
}) {
  return (
    <HeroPreviewCopyProvider>
      <HeroFeaturePreviewPanelInner view={view} activityFilter={activityFilter} />
    </HeroPreviewCopyProvider>
  );
}

function HeroFeaturePreviewPanelInner({
  view,
  activityFilter,
}: {
  view: HeroFeaturePreviewView;
  activityFilter?: string;
}) {
  const HERO_PREVIEW = useHeroPreviewCopy();
  const jobs = useMockJobs();
  const agentQuery = HERO_PREVIEW.agentQuery;

  return (
    <div className="hfs-hero-preview-embed flex h-full min-h-0 flex-col overflow-hidden bg-white text-left font-sans antialiased dark:bg-[#0f0f11]">
      <MainPreview
        activeView={view}
        jobs={jobs}
        isStatic
        agentProps={{
          phase: 'matches',
          displayQuery: agentQuery,
          showCursor: false,
          searchProgress: 100,
        }}
        activityFilter={activityFilter}
      />
    </div>
  );
}

const HeroWorkspacePreviewInner: React.FC<{ variant?: 'live' | 'static' }> = ({ variant = 'live' }) => {
  const HERO_PREVIEW = useHeroPreviewCopy();
  const isStatic = variant === 'static';
  const [activeView, setActiveView] = useState<PreviewViewId>('home');
  const [phase, setPhase] = useState<Phase>('typing');
  const [typedLength, setTypedLength] = useState(0);
  const [searchProgress, setSearchProgress] = useState(0);

  const agentQuery = HERO_PREVIEW.agentQuery;
  const agentActive = activeView === 'agent';
  const agentSimEnabled = !isStatic;
  const jobs = useMockJobs();

  useEffect(() => {
    if (!agentSimEnabled) return;
    if (!agentActive) return;
    if (phase !== 'typing') return;
    if (typedLength >= agentQuery.length) {
      const id = setTimeout(() => setPhase('searching'), PAUSE_AFTER_TYPE_MS);
      return () => clearTimeout(id);
    }
    const id = setInterval(() => {
      setTypedLength((n) => Math.min(n + 1, agentQuery.length));
    }, TYPING_MS);
    return () => clearInterval(id);
  }, [agentActive, agentQuery.length, agentSimEnabled, phase, typedLength]);

  useEffect(() => {
    if (!agentSimEnabled) return;
    if (!agentActive) return;
    if (phase !== 'searching') return;
    setSearchProgress(0);
    const started = Date.now();
    const tick = setInterval(() => {
      const pct = Math.min(100, Math.round(((Date.now() - started) / PHASE_SEARCH_MS) * 100));
      setSearchProgress(pct);
      if (pct >= 100) {
        clearInterval(tick);
        setPhase('matches');
      }
    }, 40);
    return () => clearInterval(tick);
  }, [agentActive, agentSimEnabled, phase]);

  useEffect(() => {
    if (!agentSimEnabled) return;
    if (!agentActive) return;
    if (phase === 'matches') {
      const id = setTimeout(() => {
        setPhase('typing');
        setTypedLength(0);
        setSearchProgress(0);
      }, PHASE_MATCHES_MS + PAUSE_LOOP_MS);
      return () => clearTimeout(id);
    }
    return undefined;
  }, [agentActive, agentSimEnabled, phase]);

  useEffect(() => {
    if (activeView !== 'agent') return;
    setPhase('typing');
    setTypedLength(0);
    setSearchProgress(0);
  }, [activeView]);

  const displayQuery = isStatic ? agentQuery : agentQuery.slice(0, typedLength);
  const showCursor = !isStatic && agentActive && phase === 'typing' && typedLength < agentQuery.length;
  const previewPhase: Phase = isStatic ? 'matches' : phase;

  const agentProps = isStatic
    ? {
        phase: 'matches' as Phase,
        displayQuery: agentQuery,
        showCursor: false,
        searchProgress: 100,
      }
    : {
        phase: previewPhase,
        displayQuery,
        showCursor,
        searchProgress,
      };

  const primaryNav: NavItem[] = useMemo(
    () => [
      { icon: Home, label: HERO_PREVIEW.navHome, id: 'home' },
      { icon: Briefcase, label: HERO_PREVIEW.navJobs, id: 'jobs' },
      { icon: Sparkles, label: HERO_PREVIEW.navAgent, id: 'agent' },
    ],
    [HERO_PREVIEW],
  );

  const featureNav: NavItem[] = useMemo(
    () => [
      { icon: CalendarDays, label: HERO_PREVIEW.navCalendar, id: 'calendar' },
      { icon: MessageCircle, label: HERO_PREVIEW.navCompanion, id: 'companion' },
      { icon: Video, label: HERO_PREVIEW.navPrepare, id: 'prepare' },
      { icon: Activity, label: HERO_PREVIEW.navActivity, id: 'activity' },
    ],
    [HERO_PREVIEW],
  );

  const footerNav: NavItem[] = useMemo(
    () => [
      { icon: Gift, label: HERO_PREVIEW.sidebarReferrals, id: 'referrals' },
      { icon: LifeBuoy, label: HERO_PREVIEW.sidebarSupport, id: 'support' },
      { icon: Settings, label: HERO_PREVIEW.sidebarSettings, id: 'settings' },
    ],
    [HERO_PREVIEW],
  );

  return (
    <div
      className="home-hero-preview-inner relative grid h-full min-h-0 grid-cols-1 overflow-hidden bg-white text-left font-sans antialiased dark:bg-[#0f0f11] md:grid-cols-[168px_1fr]"
    >
      <aside className="hidden h-full min-h-0 flex-col overflow-hidden border-r border-gray-200 bg-[#fafafa] p-3 dark:border-gray-800 dark:bg-[#111113] md:flex">
          <div className="mb-3 shrink-0 px-1">
            <GGJLogoMark size={22} boostVisual={false} />
          </div>

          <p className="mb-1 px-1.5 text-[8px] font-bold uppercase tracking-widest text-gray-400">
            {HERO_PREVIEW.navSectionPrimary}
          </p>
          <nav className="mb-3 space-y-0.5">
            {primaryNav.map((item) => (
              <NavButton key={item.id} item={item} active={activeView === item.id} onClick={() => setActiveView(item.id)} />
            ))}
          </nav>

          <p className="mb-1 px-1.5 text-[8px] font-bold uppercase tracking-widest text-gray-400">
            {HERO_PREVIEW.navSectionTools}
          </p>
          <nav className="min-h-0 flex-1 space-y-0.5 overflow-hidden">
            {featureNav.map((item) => (
              <NavButton key={item.id} item={item} active={activeView === item.id} onClick={() => setActiveView(item.id)} />
            ))}
          </nav>

          <div className="mt-2 shrink-0 border-t border-gray-200/80 pt-2 dark:border-gray-800">
            <p className="mb-1 px-1.5 text-[8px] font-bold uppercase tracking-widest text-gray-400">
              {HERO_PREVIEW.navSectionFooter}
            </p>
            <nav className="space-y-0.5">
              {footerNav.map((item) => (
                <NavButton key={item.id} item={item} active={activeView === item.id} onClick={() => setActiveView(item.id)} />
              ))}
            </nav>
          </div>
        </aside>

      <div className="flex h-full min-h-0 min-w-0 flex-col overflow-hidden">
          <div className="home-hero-preview-mobile-tabs flex shrink-0 gap-1.5 overflow-x-auto border-b border-gray-100 px-2 py-2 dark:border-gray-800 md:hidden [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
            {primaryNav.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.id}
                  type="button"
                  data-preview-nav={item.id}
                  onClick={() => setActiveView(item.id)}
                  className={`home-hero-preview-mobile-tabs__btn inline-flex min-h-[44px] shrink-0 items-center gap-1.5 whitespace-nowrap px-3 py-2 text-[11px] font-medium touch-manipulation ${
                    activeView === item.id
                      ? 'home-hero-preview-mobile-tabs__btn--active'
                      : ''
                  }`}
                >
                  <Icon className="h-3 w-3" />
                  {item.label}
                </button>
              );
            })}
          </div>

          <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
            <MainPreview
              activeView={activeView}
              jobs={jobs}
              isStatic={isStatic}
              agentProps={agentProps}
            />
          </div>
        </div>
    </div>
  );
};

const HeroWorkspacePreview: React.FC<{ variant?: 'live' | 'static' }> = (props) => (
  <HeroPreviewCopyProvider>
    <HeroWorkspacePreviewInner {...props} />
  </HeroPreviewCopyProvider>
);

export default HeroWorkspacePreview;
